import { useEffect, useState, useMemo, useCallback, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { format, addDays, addMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isSameDay, isSameMonth, getDay } from "date-fns";
import { ChevronLeft, ChevronRight, ChevronDown, MapPin, Phone, User, X, Wrench, Plus, Trash2, Copy, MessageSquare, CheckCircle2, Zap, Mail, Eraser, ChevronsUpDown, ChevronsDownUp, Maximize2, Minimize2, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { CategoryFilter, matchesCategories, useStickyCategories } from "@/components/common/CategoryFilter";
import { getLiveTechs, getDispatchQueue, getDispatchApprovedQueue, listDispatches, createDispatch, deleteDispatch, updateDispatch, getTechnicians, getMasterScopes, getSmartSuggest, autoScheduleDispatches, undoAutoSchedule, duplicateDispatch, toggleTechGps, addFieldNote, addDispatchNote, getFieldNotes, completeWorkOrder, updateWOLiftNote, addDispatchCardMaterial, updateTechnician, setWODotRequired, sendDispatchEmail, listContractJobs, addContractFieldNote, reorderDispatches, updateDispatchStatus, archiveContractJob, reopenContractJob } from "@/lib/api";
import { JobTag, JobBand, isContract, bandColor, NON_WO_KINDS, kindMeta, jobKindOf } from "@/components/dispatch/JobTag";
import { CrewBadge } from "@/components/tech/CrewLine";
import ContractJobModal from "@/components/dispatch/ContractJobModal";
import AddStopPopup from "@/components/dispatch/AddStopPopup";
import TimeOffModal from "@/components/TimeOffModal";
import TimeOffMenu from "@/components/TimeOffMenu";
import OffBlockBadge from "@/components/DispatchOffBlock";
import { useDispatchCategories, categoryColor, categoryBlocks } from "@/lib/dispatchCategories";
import { abbreviateName, formatNoteDate } from "@/lib/noteFormat";
import { progressKeyFor, isPartChecked } from "@/lib/cardProgress";
import { LiftNoteBadge } from "@/components/dispatch/LiftNoteBadge";
import { StatusTimeline } from "@/components/dispatch/StatusTimeline";
import SearchBar from "@/components/common/SearchBar";
import { DateJump } from "@/components/common/DateJump";
import { PillRail, Pill, PillDivider } from "@/components/common/Segmented";
import { btn } from "@/components/common/btn";
import { useAuth } from "@/auth/AuthContext";
import { NoteText } from "@/i18n/NoteText";
import { tr } from "@/i18n";
import { WeekDayHeader, WeekCornerHeader, weekGridCols } from "@/components/common/WeekGrid";
import { TechLiveState } from "@/components/dispatch/LiveTechChip";
import { DISPATCH_STATUS_LABEL } from "@/components/dispatch/dispatchConstants";
import { SearchHiContext, Hi, dispatchMatchHint, deepMatch } from "@/components/common/Highlight";
import { consumeFocusTarget, scrollToTestId, FOCUS_TECH_DISPATCH } from "@/lib/deepLinkFocus";

const HOURS_PER_DAY = 10;
// Standard work-week is Mon–Thu (4×10s). Friday/Saturday/Sunday are
// considered "off-cycle" and the auto-routines (Copy, Auto-Fill,
// Smart Dispatch) skip them — if friday/weekend coverage is needed,
// dispatchers add it manually via drag-and-drop.
// Show the server's reason (e.g. a permissions denial) instead of a blind "Failed".
const errDetail = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" && d ? `${fallback}: ${d}` : fallback;
};

const isWorkDay = (d) => {
  const dow = d.getDay();
  return dow >= 1 && dow <= 4; // 1=Mon, 4=Thu
};

export default function DispatchPage() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  // iter399 — pane width + pane state are remembered per user (same pattern as Invoicing prefs).
  const userKey = authUser?.id || authUser?.email || "anon";
  const [queue, setQueue] = useState([]);
  const [approvedQueue, setApprovedQueue] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [flashTech, setFlashTech] = useState(null);
  // Live GPS state per tech (polled every 60 s) — shown inside each tech's row label.
  const [liveByTech, setLiveByTech] = useState({});
  useEffect(() => {
    let alive = true;
    const load = () => getLiveTechs().then(r => { if (alive) setLiveByTech(Object.fromEntries((r.data?.techs || []).map(t => [t.tech_id, t]))); }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  // iter274 — Cmd+K "Dispatch · <tech>" jumps to and flashes their row.
  useEffect(() => {
    if (!technicians.length) return;
    const target = consumeFocusTarget(FOCUS_TECH_DISPATCH);
    if (!target || !technicians.some((t) => t.id === target)) return;
    setFlashTech(target);
    scrollToTestId(`tech-row-${target}`);
  }, [technicians]);
  // Separate timer so a technicians refresh mid-flash can't cancel it.
  useEffect(() => {
    if (!flashTech) return undefined;
    const timer = setTimeout(() => setFlashTech(null), 3000);
    return () => clearTimeout(timer);
  }, [flashTech]);
  const [masterScopes, setMasterScopes] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [emailingDay, setEmailingDay] = useState(false);
  const [view, setView] = useState("week");
  const [loading, setLoading] = useState(true);
  const [dragWO, setDragWO] = useState(null);
  // Contract jobs (Contract-division projects — dispatch + timesheets only) + "Add stop" popup
  const [contractModal, setContractModal] = useState(null); // { job? , then?: (saved) => void }
  const [addStop, setAddStop] = useState(null); // { techId, date }
  const [allContractJobs, setAllContractJobs] = useState([]);
  const contractJobs = useMemo(() => allContractJobs.filter(j => !j.archived), [allContractJobs]);
  const completedContractJobs = useMemo(() => allContractJobs.filter(j => j.archived), [allContractJobs]);
  const reloadContractJobs = useCallback(() => listContractJobs({ include_archived: true }).then(r => setAllContractJobs(r.data || [])).catch(() => {}), []);
  useEffect(() => { reloadContractJobs(); }, [reloadContractJobs]);
  const completeContractJob = useCallback(async (job) => {
    try { await archiveContractJob(job.id); toast.success(`${kindMeta(job.job_kind).label} marked complete — removed from the list; scheduled stops stay on the board`); reloadContractJobs(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not mark complete"); }
  }, [reloadContractJobs]);
  const reopenJob = useCallback(async (job) => {
    try { await reopenContractJob(job.id); toast.success(`${job.name} reopened`); reloadContractJobs(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reopen"); }
  }, [reloadContractJobs]);
  const [dragDispatch, setDragDispatch] = useState(null); // for moving existing dispatches
  const [hoverCell, setHoverCell] = useState(null);
  const [smartSuggestions, setSmartSuggestions] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  // iter398 — queue filters live in the toolbar; pane width + group toggles stick per browser.
  const [queueCats, setQueueCats] = useStickyCategories("abs.dispatch.queueCategories");
  const [queueSort, setQueueSortState] = useState(() => localStorage.getItem("abs.dispatch.queueSort") || "scheduled");
  const setQueueSort = (v) => { setQueueSortState(v); try { localStorage.setItem("abs.dispatch.queueSort", v); } catch { /* private mode */ } };
  const widthKey = `abs.dispatch.queueWidth.${userKey}`;
  const [queueWidth, setQueueWidth] = useState(() => Math.min(520, Math.max(200, Number(localStorage.getItem(widthKey) || localStorage.getItem("abs.dispatch.queueWidth")) || 224)));
  const persistQueueWidth = (w) => { try { localStorage.setItem(widthKey, String(w)); } catch { /* private mode */ } };
  const startQueueResize = (e) => {
    if (e.detail > 1) return; // second click of a double-click → handled by onDoubleClick reset
    e.preventDefault();
    const startX = e.clientX, startW = queueWidth;
    let last = startW;
    const move = (ev) => { last = Math.min(520, Math.max(200, startW + ev.clientX - startX)); setQueueWidth(last); };
    const up = () => {
      window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      if (last !== startW) persistQueueWidth(last);
    };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  };
  const [editModal, setEditModal] = useState(null); // dispatch being edited
  const [lastBatchId, setLastBatchId] = useState(null);
  const [teamMode, setTeamMode] = useState({});
  const [autoScheduling, setAutoScheduling] = useState(false);
  const [notesModal, setNotesModal] = useState(null); // { woId, woNumber, notes, dispatchId }
  const [monthDropPicker, setMonthDropPicker] = useState(null); // { wo, day, x, y }
  const [dispatchPopover, setDispatchPopover] = useState(null); // dispatch clicked in week or month view
  const [showWeekends, setShowWeekends] = useState(false);
  // iter400 — full-page mode: the board takes the whole window (Esc or the icon brings the app chrome back).
  const [fullPage, setFullPage] = useState(false);
  useEffect(() => {
    if (!fullPage) return;
    const onKey = (e) => { if (e.key === "Escape") setFullPage(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullPage]);
  const [autoFillPrompt, setAutoFillPrompt] = useState(null); // { wo, techId, day } — shown after drop
  const [clearConfirm, setClearConfirm] = useState(false); // clear schedule confirmation
  const copiedDatesRef = useRef({}); // tracks last copied date per "woId_techId" beyond loaded range

  const handleOpenWO = (wo) => {
    navigate("/clients", {
      state: {
        openClientId: wo.client_id,
        openTab: "workorders",
        openWorkOrderId: wo.id,
        returnTo: "/dispatch",
      },
    });
  };

  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekEnd = useMemo(() => endOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekDays = useMemo(() => {
    const all = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    return showWeekends ? all : all.filter(d => d.getDay() !== 0 && d.getDay() !== 6);
  }, [weekStart, showWeekends]);
  // Day view: reuse WeekView with a single-day array so the same
  // technician rows + drag/drop mechanics apply — no separate component.
  const dayDays = useMemo(() => [currentDate], [currentDate]);
  const monthStart = useMemo(() => startOfMonth(currentDate), [currentDate]);
  const monthEnd = useMemo(() => endOfMonth(currentDate), [currentDate]);
  const monthWeeks = useMemo(() => {
    const weeks = [];
    let dow = getDay(monthStart);
    let offset = dow === 0 ? 6 : dow - 1;
    let start = addDays(monthStart, -offset);
    for (let w = 0; w < 6; w++) {
      const week = Array.from({ length: 7 }, (_, d) => addDays(start, w * 7 + d));
      if (week.some(day => isSameMonth(day, currentDate))) weeks.push(week);
    }
    return weeks;
  }, [monthStart, currentDate]);

  const fetchRange = useMemo(() => {
    if (view === "day") return { start: format(currentDate, "yyyy-MM-dd"), end: format(currentDate, "yyyy-MM-dd") };
    if (view === "week") return { start: format(weekStart, "yyyy-MM-dd"), end: format(weekEnd, "yyyy-MM-dd") };
    const mw = monthWeeks;
    if (!mw.length) return { start: format(monthStart, "yyyy-MM-dd"), end: format(monthEnd, "yyyy-MM-dd") };
    return { start: format(mw[0][0], "yyyy-MM-dd"), end: format(mw[mw.length - 1][6], "yyyy-MM-dd") };
  }, [view, currentDate, weekStart, weekEnd, monthStart, monthEnd, monthWeeks]);

  const scopeNameMap = useMemo(() => { const m = {}; masterScopes.forEach(s => { m[s.id] = s.name; }); return m; }, [masterScopes]);
  const techScopeNames = useMemo(() => { const m = {}; technicians.forEach(t => { m[t.id] = (t.scope_ids || []).map(sid => scopeNameMap[sid] || "").filter(Boolean); }); return m; }, [technicians, scopeNameMap]);

  // Only show active, non-archived techs for dispatch
  const activeTechs = useMemo(() => technicians.filter(t => !t.is_archived), [technicians]);

  const techTimeOff = useMemo(() => {
    const m = {};
    technicians.forEach(t => {
      const dates = new Set();
      (t.time_off || []).forEach(to => {
        if (!to.start_date || !to.end_date) return;
        let d = new Date(to.start_date + "T12:00:00");
        const end = new Date(to.end_date + "T12:00:00");
        while (d <= end) { dates.add(format(d, "yyyy-MM-dd")); d = addDays(d, 1); }
      });
      m[t.id] = dates;
    });
    return m;
  }, [technicians]);

  const isTechOff = useCallback((techId, day) => techTimeOff[techId]?.has(format(day, "yyyy-MM-dd")) || false, [techTimeOff]);

  // Operator-managed day categories (PTO / Contract Job / Site Visit …).
  const dispatchCategories = useDispatchCategories();

  // Time-off entry — double-click any non-dispatch cell to pop the
  // floating quick-pick menu (PTO / Sick / Personal / Training / Holiday
  // / Custom range… / Clear cell). Multi-day ranges escalate to the
  // TimeOffModal.
  const [timeOffMenu, setTimeOffMenu] = useState(null); // { tech, dateStr, anchorRect, existingBlock }
  const [timeOffModal, setTimeOffModal] = useState(null); // { tech, initialDate, blockId? }
  // All blocks covering a day (a tech can carry e.g. a week-long Contract
  // Job plus a one-day Site Visit). The first is the "primary" — the one
  // the editor / resize handle targets; the rest render read-only.
  const findTimeOffBlocks = useCallback((techId, day) => {
    const tech = technicians.find(t => t.id === techId);
    if (!tech) return [];
    const ds = format(day, "yyyy-MM-dd");
    return (tech.time_off || [])
      .map((to, i) => (to.id ? to : { ...to, id: `legacy_${i}_${to.start_date}` }))
      .filter(to => to.start_date && to.end_date && ds >= to.start_date && ds <= to.end_date);
  }, [technicians]);
  const findTimeOffBlock = useCallback((techId, day) => findTimeOffBlocks(techId, day)[0] || null, [findTimeOffBlocks]);
  const openTimeOff = useCallback((techId, day, anchorRect) => {
    const tech = technicians.find(t => t.id === techId);
    if (!tech) return;
    const block = findTimeOffBlock(techId, day);
    setTimeOffMenu({
      tech,
      dateStr: format(day, "yyyy-MM-dd"),
      anchorRect,
      existingBlock: block,
      // iter270 — every block on the day, so the menu can list them and
      // offer "add another" instead of silently replacing the first one.
      dayBlocks: findTimeOffBlocks(techId, day),
    });
  }, [technicians, findTimeOffBlock, findTimeOffBlocks]);

  // "Blocked" = the day carries a category that forbids dispatching.
  // Non-blocking categories (Admin / Site Visit / Warranty / Contract Job)
  // still show on the board but accept work orders.
  const isTechBlocked = useCallback((techId, day) => {
    const blocks = findTimeOffBlocks(techId, day);
    return blocks.some(b => categoryBlocks(dispatchCategories, b.reason));
  }, [findTimeOffBlocks, dispatchCategories]);

  // ── Off-block drag-resize ──────────────────────────────────────────────
  // Mouse-down on the right-edge handle of an Off cell starts a drag that
  // updates `offDrag.currentDate` as the user moves across cells in the
  // same tech row. Mouse-up persists the new end_date — extending the
  // block (drag right) or shrinking it (drag left toward the start).
  const [offDrag, setOffDrag] = useState(null); // { techId, blockId, sourceStart, sourceEnd, reason, currentDate }
  // Item 17 — Drag right edge of a dispatch card to extend across days.
  // Each extra day clones the source dispatch via the duplicateDispatch
  // API; dragging back beyond the source removes the trailing clones.
  const [extendDrag, setExtendDrag] = useState(null); // { dispatchId, techId, woId, sourceDate, currentDate }
  // Multi-select state. Stores keys for both dispatches ("d:<id>") and
  // time-off blocks ("o:<techId>:<blockId>") so a single Set covers both
  // selectable types. shift = range within the same tech row; cmd/ctrl
  // = toggle individual; plain click = single-select.
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const lastClickRef = useRef(null); // { key, techId, date }
  const [reloadTick, setReloadTick] = useState(0);
  const startOffDrag = useCallback((techId, block, e) => {
    if (!block) return;
    e.preventDefault(); e.stopPropagation();
    setOffDrag({
      techId,
      blockId: block.id,
      sourceStart: block.start_date,
      sourceEnd: block.end_date,
      reason: block.reason,
      currentDate: block.end_date,
    });
  }, []);
  // Snapshot the active drag in a ref so the document-level mouseup
  // handler reads the latest value without forcing the listener to be
  // re-bound on every state tick (which would lose mid-drag events).
  const offDragRef = useRef(null);
  const techsRef = useRef([]);
  useEffect(() => { offDragRef.current = offDrag; }, [offDrag]);
  useEffect(() => { techsRef.current = technicians; }, [technicians]);
  useEffect(() => {
    if (!offDrag) return;
    const onMove = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      let n = el;
      while (n && n !== document.body) {
        const tid = n.getAttribute && n.getAttribute("data-testid");
        if (tid && tid.startsWith(`cell-${offDrag.techId}-`)) {
          const date = tid.slice(`cell-${offDrag.techId}-`.length);
          if (/^\d{4}-\d{2}-\d{2}$/.test(date) && date !== offDragRef.current?.currentDate) {
            setOffDrag(prev => prev ? { ...prev, currentDate: date } : prev);
          }
          return;
        }
        n = n.parentElement;
      }
    };
    const onUp = async () => {
      const drag = offDragRef.current;
      setOffDrag(null);
      if (!drag) return;
      const tech = techsRef.current.find(t => t.id === drag.techId);
      if (!tech) return;
      const newEnd = drag.currentDate < drag.sourceStart ? drag.sourceStart : drag.currentDate;
      if (newEnd === drag.sourceEnd) return;
      const arr = [...(tech.time_off || [])];
      let idx = arr.findIndex(t => t.id === drag.blockId);
      if (idx < 0) idx = arr.findIndex(t => t.start_date === drag.sourceStart && t.end_date === drag.sourceEnd && t.reason === drag.reason);
      if (idx < 0) return;
      arr[idx] = { ...arr[idx], id: arr[idx].id || drag.blockId || ("to_" + Math.random().toString(36).slice(2, 10)), end_date: newEnd };
      try {
        await updateTechnician(tech.id, { time_off: arr });
        toast.success(newEnd > drag.sourceEnd ? "Extended" : "Shortened");
        setReloadTick(t => t + 1);
      } catch (err) {
        toast.error("Save failed: " + (err.response?.data?.detail || err.message));
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [offDrag]);

  // ── Dispatch-card drag-to-extend (Item 17) ─────────────────────────────
  // Mirror of the off-block drag handler. Mouse-down on the right-edge
  // grip starts the drag; mousemove tracks which date cell the cursor
  // is over; mouseup persists by cloning into each new day or deleting
  // trailing duplicates when the user drags back.
  const extendDragRef = useRef(null);
  const dispatchesRef = useRef([]);
  useEffect(() => { extendDragRef.current = extendDrag; }, [extendDrag]);
  useEffect(() => { dispatchesRef.current = dispatches; }, [dispatches]);
  const startExtendDrag = useCallback((d, e) => {
    if (!d) return;
    e.preventDefault(); e.stopPropagation();
    setExtendDrag({
      dispatchId: d.id,
      techId: d.tech_id,
      woId: d.wo_id,
      sourceDate: d.date,
      currentDate: d.date,
    });
  }, []);
  useEffect(() => {
    if (!extendDrag) return;
    const onMove = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      let n = el;
      while (n && n !== document.body) {
        const tid = n.getAttribute && n.getAttribute("data-testid");
        if (tid && tid.startsWith(`cell-${extendDrag.techId}-`)) {
          const date = tid.slice(`cell-${extendDrag.techId}-`.length);
          if (/^\d{4}-\d{2}-\d{2}$/.test(date) && date !== extendDragRef.current?.currentDate) {
            setExtendDrag(prev => prev ? { ...prev, currentDate: date } : prev);
          }
          return;
        }
        n = n.parentElement;
      }
    };
    const onUp = async () => {
      const drag = extendDragRef.current;
      setExtendDrag(null);
      if (!drag) return;
      const target = drag.currentDate < drag.sourceDate ? drag.sourceDate : drag.currentDate;
      if (target === drag.sourceDate) return;
      try {
        // Walk forward (extension) — clone the source dispatch once per
        // day that doesn't already have a dispatch for this WO+tech.
        const existing = new Set(
          dispatchesRef.current
            .filter(x => x.tech_id === drag.techId && x.wo_id === drag.woId)
            .map(x => x.date)
        );
        const cur = new Date(drag.sourceDate + "T12:00:00");
        const end = new Date(target + "T12:00:00");
        let cloned = 0;
        while (cur < end) {
          cur.setDate(cur.getDate() + 1);
          const ds = cur.toISOString().slice(0, 10);
          if (!existing.has(ds)) {
            await duplicateDispatch(drag.dispatchId, { date: ds });
            cloned += 1;
          }
        }
        if (cloned > 0) toast.success(`Extended across ${cloned} additional day${cloned === 1 ? "" : "s"}`);
        // Walk backward (shrink) — delete dispatches for this WO+tech
        // that fall AFTER the new target date.
        if (target < drag.sourceDate) {
          // Shouldn't happen given the guard above, but kept for parity
          // with the off-block logic.
        } else {
          const toRemove = dispatchesRef.current.filter(x =>
            x.tech_id === drag.techId &&
            x.wo_id === drag.woId &&
            x.date > target &&
            x.id !== drag.dispatchId
          );
          for (const d of toRemove) {
            try { await deleteDispatch(d.id); } catch { /* non-fatal */ }
          }
        }
        setReloadTick(t => t + 1);
      } catch (err) {
        toast.error("Extend failed: " + (err.response?.data?.detail || err.message));
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [extendDrag]);

  // #166 (Group H): persist stable ids on legacy time-off blocks the
  // moment they're loaded so TimeOffModal / removeOffBlock can match
  // them. Older blocks (from before the id field was added) come back
  // without one and would otherwise be unaddressable. We patch the
  // tech once per page load — no-op when every block already has an
  // id. Mutates the in-memory list before setTechnicians so the UI
  // sees ids immediately.
  const _backfillLegacyTimeOffIds = useCallback(async (techs) => {
    const patched = [];
    for (const t of techs || []) {
      const blocks = Array.isArray(t.time_off) ? t.time_off : [];
      if (!blocks.length || blocks.every(b => b && b.id)) {
        patched.push(t);
        continue;
      }
      const fixed = blocks.map((b) => b && b.id ? b : {
        ...(b || {}),
        id: "to_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4),
      });
      try {
        await updateTechnician(t.id, { time_off: fixed });
      } catch (_e) {
        // Swallow — UI still works with in-memory ids; next save persists them.
      }
      patched.push({ ...t, time_off: fixed });
    }
    return patched;
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [qR, aR, dR, tR, sR] = await Promise.all([getDispatchQueue(), getDispatchApprovedQueue(), listDispatches({ start_date: fetchRange.start, end_date: fetchRange.end }), getTechnicians(), getMasterScopes()]);
      setQueue(qR.data); setApprovedQueue(aR.data || []); setDispatches(dR.data);
      const techsWithIds = await _backfillLegacyTimeOffIds(tR.data || []);
      setTechnicians(techsWithIds);
      setMasterScopes(sR.data);
    } catch { toast.error("Failed to load dispatch data"); }
    finally { setLoading(false); }
  }, [fetchRange, _backfillLegacyTimeOffIds]);

  useEffect(() => { loadData(); }, [loadData, reloadTick]);

  const getDispatchesForCell = useCallback((techId, day) => {
    const ds = format(day, "yyyy-MM-dd");
    return dispatches.filter(d => d.tech_id === techId && d.date === ds).sort((a, b) => (a.stop_order ?? 0) - (b.stop_order ?? 0) || (a.created_at || "").localeCompare(b.created_at || ""));
  }, [dispatches]);
  const getTechDayHours = useCallback((techId, day) => getDispatchesForCell(techId, day).reduce((s, d) => s + (d.hours || 0), 0), [getDispatchesForCell]);
  const doesTechMatchWO = useCallback((techId, wo) => {
    if (!wo?.wo_scopes?.length) return true;
    const ts = techScopeNames[techId] || [];
    if (!ts.length) return true; // Allow drop on techs with no scopes assigned
    return wo.wo_scopes.some(ws => ts.some(t => t.toLowerCase() === ws.toLowerCase()));
  }, [techScopeNames]);

  const loadSmartSuggest = async (woId) => {
    try { const res = await getSmartSuggest(woId); setSmartSuggestions(prev => ({ ...prev, [woId]: res.data })); } catch {}
  };

  const handleAutoSchedule = async (mode) => {
    setAutoScheduling(true);
    try {
      const refDate = format(currentDate, "yyyy-MM-dd");
      const res = await autoScheduleDispatches({ mode, reference_date: refDate });
      if (res.data.created_count === 0) {
        toast.info("No dispatches to create — all WOs are already fully dispatched or no techs available.");
      } else {
        setLastBatchId(res.data.batch_id);
        toast.success(`Auto-dispatched ${res.data.created_count} assignments`);
      }
      const [qR, aR, dR] = await Promise.all([getDispatchQueue(), getDispatchApprovedQueue(), listDispatches({ start_date: fetchRange.start, end_date: fetchRange.end })]);
      setQueue(qR.data); setApprovedQueue(aR.data || []); setDispatches(dR.data);
    } catch { toast.error("Auto-schedule failed"); }
    finally { setAutoScheduling(false); }
  };

  const handleUndoAutoSchedule = async () => {
    if (!lastBatchId) return;
    try {
      const res = await undoAutoSchedule({ batch_id: lastBatchId });
      toast.success(`Undid ${res.data.deleted_count} auto-dispatched assignments`);
      setLastBatchId(null);
      const [qR, aR, dR] = await Promise.all([getDispatchQueue(), getDispatchApprovedQueue(), listDispatches({ start_date: fetchRange.start, end_date: fetchRange.end })]);
      setQueue(qR.data); setApprovedQueue(aR.data || []); setDispatches(dR.data);
    } catch { toast.error("Undo failed"); }
  };

  const handleDrop = async (wo, techId, day) => {
    if (NON_WO_KINDS.includes(wo?.job_kind)) return handleContractDrop(wo, techId, day);
    // Determine which techs to dispatch
    const assignedIds = wo.assigned_tech_ids?.length ? wo.assigned_tech_ids : [];
    let techsToDispatch = assignedIds.length > 1 ? assignedIds : [techId];
    // If team mode is on for this WO, add the teammate of the dropped tech
    if (teamMode[wo.id] && techsToDispatch.length === 1) {
      const tech = technicians.find(t => t.id === techId);
      if (tech?.teammate_id && !techsToDispatch.includes(tech.teammate_id)) {
        techsToDispatch = [...techsToDispatch, tech.teammate_id];
      }
    }
    const dateStr = format(day, "yyyy-MM-dd");
    let dispatched = 0;
    let failed = 0;
    for (const tid of techsToDispatch) {
      // #63 — Time-off conflict-skip. Both the frontend and the backend
      // (POST /api/dispatches) reject drops on a tech's time-off date.
      // We pre-check here so the toast is instant and we avoid an HTTP
      // round-trip; the backend 409 is the belt-and-braces guard.
      if (isTechBlocked(tid, day)) {
        const tname = (technicians.find(t => t.id === tid) || {}).name || "Technician";
        const friendlyDate = format(day, "MMM d, yyyy");
        toast.error(`${tname} is off on ${friendlyDate} — dispatch not created`);
        if (techsToDispatch.length > 1) { failed++; continue; }
        return;
      }
      const techDayHrs = getTechDayHours(tid, day);
      const available = Math.max(0, HOURS_PER_DAY - techDayHrs);
      const woRemainingOrFull = wo.remaining_hours > 0 ? wo.remaining_hours : HOURS_PER_DAY;
      // A tech can take multiple jobs a day — once the day is full we
      // silently overbook with the WO's remaining hours instead of
      // blocking the drop.
      const hours = available > 0 ? Math.min(woRemainingOrFull, available) : woRemainingOrFull;
      try {
        await createDispatch({ wo_id: wo.id, tech_id: tid, date: dateStr, hours });
        dispatched++;
      } catch (e) {
        // Surface the backend's 409 time-off message if the client cache
        // was stale and the pre-check missed (e.g. a manager added a
        // time-off block in another tab seconds before the drop).
        if (e?.response?.status === 409 && e?.response?.data?.detail) {
          toast.error(e.response.data.detail);
        }
        failed++;
      }
    }
    const [qR, aR, dR] = await Promise.all([getDispatchQueue(), getDispatchApprovedQueue(), listDispatches({ start_date: fetchRange.start, end_date: fetchRange.end })]);
    setQueue(qR.data); setApprovedQueue(aR.data || []); setDispatches(dR.data);
    if (dispatched > 0) toast.success(`Dispatched ${wo.wo_number} — ${dispatched} tech${dispatched > 1 ? "s" : ""}`);
    if (failed > 0) toast.error(`${failed} tech${failed > 1 ? "s" : ""} could not be dispatched`);
    setHoverCell(null);
    // Show auto-fill prompt if WO has remaining hours
    const updatedWO = qR.data.find(q => q.id === wo.id);
    if (updatedWO && updatedWO.remaining_hours > 0) {
      setAutoFillPrompt({ wo: updatedWO, techId, day });
    }
  };

  const refreshBoard = async () => {
    const [qR, aR, dR] = await Promise.all([getDispatchQueue(), getDispatchApprovedQueue(), listDispatches({ start_date: fetchRange.start, end_date: fetchRange.end })]);
    setQueue(qR.data); setApprovedQueue(aR.data || []); setDispatches(dR.data);
  };

  // Contract stop — same card as a WO, no WO behind it. Hours = what's left of the day (min 1h).
  const handleContractDrop = async (job, techId, day, endDay) => {
    if (endDay && endDay > day) return handleContractRange(job, techId, day, endDay);
    const dateStr = format(day, "yyyy-MM-dd");
    if (isTechBlocked(techId, day)) {
      const tname = (technicians.find(t => t.id === techId) || {}).name || "Technician";
      toast.error(`${tname} is off on ${format(day, "MMM d, yyyy")} — dispatch not created`);
      return;
    }
    const available = Math.max(0, HOURS_PER_DAY - getTechDayHours(techId, day));
    try {
      await createDispatch({ contract_job_id: job.id, tech_id: techId, date: dateStr, hours: available > 0 ? available : HOURS_PER_DAY });
      await refreshBoard();
      toast.success(`Dispatched ${kindMeta(job.job_kind).tag} · ${job.name}`);
    } catch (e) { toast.error(errDetail(e, "Failed to dispatch contract job")); }
    setHoverCell(null);
  };

  // Multi-day contract: one stop per weekday in range; off days are skipped, not failed.
  const handleContractRange = async (job, techId, startDay, endDay) => {
    let made = 0; const skipped = [];
    for (let d = new Date(startDay); d <= endDay; d = addDays(d, 1)) {
      const dow = d.getDay();
      if (dow === 0 || dow === 6) continue;
      if (isTechBlocked(techId, d)) { skipped.push(format(d, "MMM d")); continue; }
      const available = Math.max(0, HOURS_PER_DAY - getTechDayHours(techId, d));
      try {
        await createDispatch({ contract_job_id: job.id, tech_id: techId, date: format(d, "yyyy-MM-dd"), hours: available > 0 ? available : HOURS_PER_DAY });
        made += 1;
      } catch (e) { skipped.push(format(d, "MMM d")); }
    }
    await refreshBoard().catch(() => {});
    reloadContractJobs();
    if (made) toast.success(`Dispatched ${kindMeta(job.job_kind).tag} · ${job.name} · ${made} day${made === 1 ? "" : "s"}`);
    if (skipped.length) toast.warning(`Skipped (off / not dispatchable): ${skipped.join(", ")}`);
  };

  // "Add stop" from a card → another WO (queue rule) or contract job on the same tech/day
  const handleAddStopPick = async ({ kind, item, endDate }) => {
    const { techId, date } = addStop || {};
    setAddStop(null);
    if (!techId || !date) return;
    const day = new Date(`${date}T12:00:00`);
    if (kind === "contract") return handleContractDrop({ ...item, job_kind: item.job_kind || "contract" }, techId, day, endDate ? new Date(`${endDate}T12:00:00`) : null);
    return handleDrop(item, techId, day);
  };

  // Stops reorder by drag (one tech/day) — optimistic, then persisted
  const handleReorderStops = async (order) => {
    setDispatches(prev => prev.map(d => (order.includes(d.id) ? { ...d, stop_order: order.indexOf(d.id) } : d)));
    try { await reorderDispatches(order); } catch (e) { toast.error(errDetail(e, "Could not reorder stops")); }
  };

  const handleRemoveDispatch = async (dispatchId) => {
    try {
      await deleteDispatch(dispatchId);
      const [qR, aR, dR] = await Promise.all([getDispatchQueue(), getDispatchApprovedQueue(), listDispatches({ start_date: fetchRange.start, end_date: fetchRange.end })]);
      setQueue(qR.data); setApprovedQueue(aR.data || []); setDispatches(dR.data);
      toast.success("Dispatch removed");
    } catch (e) { toast.error(errDetail(e, "Failed to remove dispatch")); }
  };

  // ── Multi-select helpers ────────────────────────────────────────────────
  // selectKey shape:
  //   d:<dispatchId>                  → real dispatch
  //   o:<techId>:<blockId>:<reason>   → time-off block (PTO/Personal/etc.)
  // The key encodes the type so bulk-delete/drag handlers can route each
  // entry to the right backend call without re-fetching parent objects.
  const dispatchById = useMemo(() => {
    const m = {}; (dispatches || []).forEach(d => { m[d.id] = d; }); return m;
  }, [dispatches]);
  const isSelected = useCallback((key) => selectedKeys.has(key), [selectedKeys]);
  const handleToggleSelect = useCallback((key, e, ctx) => {
    // ctx: { type:"dispatch"|"off", techId, date }
    const meta = e?.metaKey || e?.ctrlKey;
    const shift = e?.shiftKey;
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (shift && lastClickRef.current && lastClickRef.current.techId === ctx.techId) {
        // Range-select within the same tech row, between the two click dates.
        const a = lastClickRef.current.date, b = ctx.date;
        const start = a < b ? a : b;
        const end = a < b ? b : a;
        const techDisp = (dispatches || []).filter(d => d.tech_id === ctx.techId && d.date >= start && d.date <= end);
        techDisp.forEach(d => next.add(`d:${d.id}`));
        // Also pull in any time-off blocks that overlap the range for that tech.
        const tech = (technicians || []).find(t => t.id === ctx.techId);
        for (const blk of (tech?.time_off || [])) {
          if (!blk?.id) continue;
          const bs = blk.start_date, be = blk.end_date || blk.start_date;
          if (bs <= end && be >= start) next.add(`o:${ctx.techId}:${blk.id}:${blk.reason || ""}`);
        }
      } else if (meta) {
        // Toggle the clicked card without disturbing existing selection.
        if (next.has(key)) next.delete(key); else next.add(key);
      } else {
        // Plain click — single select (clears anything else).
        next.clear();
        next.add(key);
      }
      return next;
    });
    lastClickRef.current = { key, techId: ctx.techId, date: ctx.date };
  }, [dispatches, technicians]);
  const clearSelection = useCallback(() => { setSelectedKeys(new Set()); lastClickRef.current = null; }, []);

  // Remove a time-off block from a tech's roster (no API endpoint for it
  // — we patch the whole technician with the filtered array).
  const removeOffBlock = useCallback(async (techId, blockId) => {
    const tech = (technicians || []).find(t => t.id === techId);
    if (!tech) return;
    const next = (tech.time_off || []).filter(b => b.id !== blockId);
    await updateTechnician(techId, { time_off: next });
  }, [technicians]);

  // Bulk delete — runs sequentially so a failing call doesn't kill the
  // batch. PTO/time-off blocks always delete; real dispatches delete too.
  const handleBulkDelete = useCallback(async () => {
    if (selectedKeys.size === 0) return;
    let dispCount = 0, offCount = 0, failed = 0;
    for (const key of Array.from(selectedKeys)) {
      try {
        if (key.startsWith("d:")) {
          await deleteDispatch(key.slice(2));
          dispCount += 1;
        } else if (key.startsWith("o:")) {
          const [, techId, blockId] = key.split(":");
          await removeOffBlock(techId, blockId);
          offCount += 1;
        }
      } catch { failed += 1; }
    }
    clearSelection();
    setReloadTick(t => t + 1);
    const parts = [];
    if (dispCount) parts.push(`${dispCount} dispatch${dispCount > 1 ? "es" : ""}`);
    if (offCount) parts.push(`${offCount} time-off block${offCount > 1 ? "s" : ""}`);
    if (parts.length) toast.success(`Removed ${parts.join(" + ")}`);
    if (failed) toast.error(`${failed} item(s) failed to delete`);
  }, [selectedKeys, removeOffBlock, clearSelection]);

  // Drag any selected card → drop on Open WOs sidebar. For real dispatches
  // we delete (effectively "send back to queue"); for time-off blocks we
  // also delete since they're not real WOs to begin with.
  const handleBulkSendToQueue = useCallback(async () => {
    // Same effect as bulk delete from a backend standpoint — but the toast
    // wording reflects the dispatcher's intent.
    if (selectedKeys.size === 0) return;
    let dispCount = 0, offCount = 0;
    for (const key of Array.from(selectedKeys)) {
      try {
        if (key.startsWith("d:")) { await deleteDispatch(key.slice(2)); dispCount += 1; }
        else if (key.startsWith("o:")) {
          const [, techId, blockId] = key.split(":");
          await removeOffBlock(techId, blockId); offCount += 1;
        }
      } catch { /* ignore — bulk best-effort */ }
    }
    clearSelection();
    setReloadTick(t => t + 1);
    if (dispCount) toast.success(`Returned ${dispCount} dispatch${dispCount > 1 ? "es" : ""} to Open WOs`);
    if (offCount) toast.success(`Removed ${offCount} time-off block${offCount > 1 ? "s" : ""}`);
  }, [selectedKeys, removeOffBlock, clearSelection]);

  // Keyboard: Delete / Backspace nukes the current selection. Esc clears.
  useEffect(() => {
    if (selectedKeys.size === 0) return;
    const onKey = (e) => {
      const target = e.target;
      const isFormField = target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if (isFormField) return;
      if (e.key === "Escape") { clearSelection(); }
      else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        handleBulkDelete();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedKeys, handleBulkDelete, clearSelection]);

  // Move existing dispatch to a new tech+day
  const handleMoveDispatch = async (dispatch, newTechId, day) => {
    if (isTechBlocked(newTechId, day)) { toast.error("Tech is off this day"); return; }
    const dateStr = format(day, "yyyy-MM-dd");
    if (dispatch.tech_id === newTechId && dispatch.date === dateStr) return; // no change
    try {
      await updateDispatch(dispatch.id, { tech_id: newTechId, date: dateStr });
      const [qR, aR, dR] = await Promise.all([getDispatchQueue(), getDispatchApprovedQueue(), listDispatches({ start_date: fetchRange.start, end_date: fetchRange.end })]);
      setQueue(qR.data); setApprovedQueue(aR.data || []); setDispatches(dR.data);
      toast.success(`Moved ${dispatch.wo_number || "dispatch"} → ${dateStr}`);
    } catch (e) { toast.error(errDetail(e, "Failed to move dispatch")); }
    finally { setHoverCell(null); setDragDispatch(null); }
  };

  // Save dispatch edits (parts_override, notes)
  const handleSaveDispatchEdit = async (dispatchId, updates) => {
    try {
      await updateDispatch(dispatchId, updates);
      const dR = await listDispatches({ start_date: fetchRange.start, end_date: fetchRange.end });
      setDispatches(dR.data);
      setEditModal(null);
      toast.success("Dispatch updated");
    } catch (e) { toast.error(errDetail(e, "Failed to update dispatch")); }
  };

  const getNextWeekday = useCallback((dispatch) => {
    const key = `${dispatch.wo_id}_${dispatch.tech_id}`;
    // Find all dispatches for this same WO + tech in loaded range
    const sameWoTech = dispatches.filter(d => d.wo_id === dispatch.wo_id && d.tech_id === dispatch.tech_id);
    const dates = sameWoTech.map(d => new Date(d.date + "T12:00:00"));
    dates.push(new Date(dispatch.date + "T12:00:00"));
    // Also check the ref for copies made beyond the loaded range
    const lastCopied = copiedDatesRef.current[key];
    if (lastCopied) dates.push(new Date(lastCopied + "T12:00:00"));
    const latest = new Date(Math.max(...dates));
    let next = addDays(latest, 1);
    // Skip Fri/Sat/Sun by default — Mon-Thu work week.
    while (!isWorkDay(next)) {
      next = addDays(next, 1);
    }
    return format(next, "yyyy-MM-dd");
  }, [dispatches]);

  const handleDuplicateDispatch = async (dispatch, targetDate, targetTechId) => {
    try {
      await duplicateDispatch(dispatch.id, {
        date: targetDate,
        tech_id: targetTechId || dispatch.tech_id,
        hours: dispatch.hours,
      });
      // Track this date so the next copy goes to the following weekday
      const key = `${dispatch.wo_id}_${dispatch.tech_id}`;
      copiedDatesRef.current[key] = targetDate;
      const [qR, aR, dR] = await Promise.all([getDispatchQueue(), getDispatchApprovedQueue(), listDispatches({ start_date: fetchRange.start, end_date: fetchRange.end })]);
      setQueue(qR.data); setApprovedQueue(aR.data || []); setDispatches(dR.data);
      toast.success(`Copied dispatch to ${targetDate}`);
    } catch (e) { toast.error(errDetail(e, "Failed to duplicate dispatch")); }
  };

  const handleToggleTechGps = async (techId, currentEnabled) => {
    const newVal = !currentEnabled;
    try {
      await toggleTechGps({ tech_id: techId, enabled: newVal });
      setTechnicians(prev => prev.map(t => t.id === techId ? { ...t, gps_enabled: newVal } : t));
      toast.success(`GPS ${newVal ? "enabled" : "disabled"} for tech`);
    } catch (e) { toast.error(errDetail(e, "Failed to toggle GPS")); }
  };

  const handleCompleteWO = async (d) => {
    try {
      if (isContract(d)) {
        await updateDispatchStatus(d.id, { status: "completed" });
        await refreshBoard();
        toast.success(`${kindMeta(jobKindOf(d)).tag} · ${d.wo_title || "stop"} marked complete`);
        return;
      }
      await completeWorkOrder(d.wo_id);
      const [qR, aR, dR] = await Promise.all([getDispatchQueue(), getDispatchApprovedQueue(), listDispatches({ start_date: fetchRange.start, end_date: fetchRange.end })]);
      setQueue(qR.data); setApprovedQueue(aR.data || []); setDispatches(dR.data);
      toast.success(`WO ${d.wo_number || ""} marked complete`);
    } catch (e) { toast.error(errDetail(e, "Failed to complete work order")); }
  };

  const handleAutoFill = async () => {
    if (!autoFillPrompt) return;
    const { wo, techId, day } = autoFillPrompt;
    setAutoFillPrompt(null);
    let remaining = wo.remaining_hours;
    let currentDay = addDays(day, 1);
    let filled = 0;
    // #246 (iter168): hard horizon guard so a tech who is off across every
    // upcoming workday (e.g. extended vacation, terminated) cannot hang the
    // tab in an unbounded scan. 365 days is well past any real scheduling
    // window; if we don't find a slot inside that, surface a toast.
    let guard = 0;
    let exhausted = false;
    while (remaining > 0) {
      if (guard++ >= 365) { exhausted = true; break; }
      // Skip Fri/Sat/Sun — Mon-Thu work week (4×10s).
      while (!isWorkDay(currentDay)) {
        currentDay = addDays(currentDay, 1);
      }
      if (isTechBlocked(techId, currentDay)) { currentDay = addDays(currentDay, 1); continue; }
      const hours = Math.min(remaining, HOURS_PER_DAY);
      try {
        await createDispatch({ wo_id: wo.id, tech_id: techId, date: format(currentDay, "yyyy-MM-dd"), hours });
        remaining -= hours;
        filled++;
      } catch { break; }
      currentDay = addDays(currentDay, 1);
    }
    const [qR, aR, dR] = await Promise.all([getDispatchQueue(), getDispatchApprovedQueue(), listDispatches({ start_date: fetchRange.start, end_date: fetchRange.end })]);
    setQueue(qR.data); setApprovedQueue(aR.data || []); setDispatches(dR.data);
    if (filled > 0) toast.success(`Auto-filled ${filled} more day${filled > 1 ? "s" : ""} for ${wo.wo_number}`);
    if (exhausted) toast.error(`Couldn't auto-fill — no schedulable day found within the next year (tech off all upcoming workdays?)`);
  };

  const handleClearSchedule = async () => {
    setClearConfirm(false);
    const today = format(new Date(), "yyyy-MM-dd");
    // Only delete future dispatches (after today) within the current view range
    const futureDispatches = dispatches.filter(d => d.date > today);
    if (futureDispatches.length === 0) { toast.info("No future dispatches to clear"); return; }
    let deleted = 0;
    for (const d of futureDispatches) {
      try { await deleteDispatch(d.id); deleted++; } catch {}
    }
    const [qR, aR, dR] = await Promise.all([getDispatchQueue(), getDispatchApprovedQueue(), listDispatches({ start_date: fetchRange.start, end_date: fetchRange.end })]);
    setQueue(qR.data); setApprovedQueue(aR.data || []); setDispatches(dR.data);
    toast.success(`Cleared ${deleted} future dispatch${deleted !== 1 ? "es" : ""}`);
  };

  const openNotesModal = async (dispatch) => {
    try {
      const res = await getFieldNotes(dispatch.wo_id);
      setNotesModal({ woId: dispatch.wo_id, woNumber: dispatch.wo_number, woTitle: dispatch.wo_title, notes: res.data });
    } catch {
      setNotesModal({ woId: dispatch.wo_id, woNumber: dispatch.wo_number, woTitle: dispatch.wo_title, notes: dispatch.field_notes || [] });
    }
  };

  const handleAddNote = async (woId, noteText) => {
    try {
      await addFieldNote(woId, { text: noteText, tech_name: "Dispatch" });
      const res = await getFieldNotes(woId);
      setNotesModal(prev => prev ? { ...prev, notes: res.data } : null);
      // Refresh dispatches to update note counts
      const dR = await listDispatches({ start_date: fetchRange.start, end_date: fetchRange.end });
      setDispatches(dR.data);
      toast.success("Note added");
    } catch (e) { toast.error(errDetail(e, "Failed to add note")); }
  };

  // Global search filter for dispatches
  const filteredDispatches = useMemo(() => {
    if (!searchQuery.trim()) return dispatches;
    const q = searchQuery.toLowerCase();
    const techName = (id) => technicians.find(t => t.id === id)?.name || "";
    // Any text on the dispatch (deep — WO, customer, site, contact, tech, status, PO #, equipment, parts, every note).
    return dispatches.filter(d => deepMatch({ ...d, tech_name: techName(d.tech_id), status_label: (d.dispatch_status || d.status || "").replace(/_/g, " ") }, q));
  }, [dispatches, searchQuery, technicians]);

  // Filter techs that have matching dispatches (or whose own name/department matches) when searching
  const matchedTechIds = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const ids = new Set(filteredDispatches.map(d => d.tech_id));
    technicians.forEach(t => { if (deepMatch(t, searchQuery)) ids.add(t.id); });
    return ids;
  }, [filteredDispatches, searchQuery, technicians]);

  const getFilteredDispatchesForCell = useCallback((techId, day) => {
    const ds = format(day, "yyyy-MM-dd");
    return filteredDispatches.filter(d => d.tech_id === techId && d.date === ds).sort((a, b) => (a.stop_order ?? 0) - (b.stop_order ?? 0) || (a.created_at || "").localeCompare(b.created_at || ""));
  }, [filteredDispatches]);

  // Header summary for the days on screen: hours booked vs. capacity (techs × Mon–Thu days not off × 10h).
  const hoursSummary = useMemo(() => {
    const days = view === "month"
      ? Array.from({ length: monthEnd.getDate() }, (_, i) => addDays(monthStart, i))
      : view === "day" ? dayDays : weekDays;
    const dayKeys = new Set(days.map(d => format(d, "yyyy-MM-dd")));
    const techIds = new Set(activeTechs.map(t => t.id));
    const booked = dispatches.reduce((s, d) => s + (techIds.has(d.tech_id) && dayKeys.has(d.date) ? (d.hours || 0) : 0), 0);
    const workDays = days.filter(d => d.getDay() >= 1 && d.getDay() <= 4);
    const available = activeTechs.reduce((s, t) => s + workDays.filter(d => !isTechOff(t.id, d)).length * HOURS_PER_DAY, 0);
    return { booked: Math.round(booked * 10) / 10, available };
  }, [view, monthStart, monthEnd, dayDays, weekDays, dispatches, activeTechs, isTechOff]);

  const prev = () => setCurrentDate(p => view === "day" ? addDays(p, -1) : view === "week" ? addDays(p, -7) : addMonths(p, -1));
  const next = () => setCurrentDate(p => view === "day" ? addDays(p, 1) : view === "week" ? addDays(p, 7) : addMonths(p, 1));
  const goToday = () => setCurrentDate(new Date());

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900" /></div>;

  const dateLabel = view === "day"
    ? format(currentDate, "EEE, MMM d, yyyy")
    : view === "week"
      ? (isSameMonth(weekStart, weekEnd) ? `${format(weekStart, "MMM d")}–${format(weekEnd, "d, yyyy")}` : `${format(weekStart, "MMM d")} – ${format(weekEnd, "MMM d, yyyy")}`)
      : format(currentDate, "MMMM yyyy");

  return (
    <SearchHiContext.Provider value={searchQuery}>
    <div className={fullPage ? "fixed inset-0 z-[70] overflow-auto bg-slate-100 p-3 pt-5 flex flex-col gap-2 dark:bg-[hsl(220_10%_8%)]" : "relative flex flex-col gap-2"} data-testid="dispatch-page" data-fullpage={fullPage ? "true" : "false"}>
      
        <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="dispatch-toolbar">
          <div className="flex min-w-0 items-center gap-1 flex-nowrap [&>*]:shrink-0">
            <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder={tr("Search...")} width="w-[8.5rem] shrink-0" testId="dispatch-search" />
            <PillRail testId="dispatch-filter-pills">
              <CategoryFilter records={[...queue, ...approvedQueue]} value={queueCats} onChange={setQueueCats} testId="queue-category-filter" pill />
              <span className="relative inline-flex">
                <select value={queueSort} onChange={(e) => setQueueSort(e.target.value)} data-testid="queue-sort"
                  className={`appearance-none cursor-pointer whitespace-nowrap rounded-full bg-transparent pl-3 pr-7 py-1 text-xs font-medium outline-none transition-colors ${queueSort !== "scheduled" ? "seg-on" : "seg-off"}`} title={tr("Sort the work order pane")}>
                  <option value="scheduled">{tr("Date")}</option>
                  <option value="wo_number">{tr("WO #")}</option>
                  <option value="customer">{tr("Customer")}</option>
                  <option value="site">{tr("Site")}</option>
                  <option value="oldest">{tr("Oldest")}</option>
                  <option value="hours">{tr("Hours")}</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3 w-3 -translate-y-1/2 opacity-70 text-slate-500" />
              </span>
            </PillRail>
            <div className="h-4 w-px bg-slate-200" />
            <PillRail testId="dispatch-view-pills">
              <Pill on={view === "day"} onClick={() => setView("day")} testId="btn-view-day" title="Today only — extra wide, larger type">{tr("Day")}</Pill>
              <Pill on={view === "week"} onClick={() => setView("week")} testId="btn-view-week">{tr("Week")}</Pill>
              <Pill on={view === "month"} onClick={() => setView("month")} testId="btn-view-month">{tr("Month")}</Pill>
              <PillDivider />
              <Pill on={showWeekends} onClick={() => setShowWeekends(v => !v)} testId="weekends-toggle" title={showWeekends ? "Hide Saturday & Sunday" : "Show Saturday & Sunday"}>{tr("Wknd")}</Pill>
            </PillRail>
            <div className="h-4 w-px bg-slate-200" />
            {/* iter301 — email the day's schedule to every tech who has
                jobs on it. Same send the daily cron uses. */}
            <button
              type="button"
              className={`${btn("data")} h-8 w-8 justify-center !px-0`}
              disabled={emailingDay}
              onClick={async () => {
                const day = format(currentDate, "yyyy-MM-dd");
                if (!window.confirm(`Email the ${day} schedule to every tech with jobs that day?`)) return;
                setEmailingDay(true);
                try {
                  const { data } = await sendDispatchEmail({ date: day });
                  toast.success(`${data.sent} email${data.sent === 1 ? "" : "s"} sent`
                    + (data.skipped ? ` · ${data.skipped} skipped (nothing scheduled or no email)` : "")
                    + (data.failed ? ` · ${data.failed} failed` : ""));
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not send the schedule");
                } finally { setEmailingDay(false); }
              }}
              data-testid="btn-email-day-schedule"
              title={emailingDay ? "Sending…" : "Email today's schedule to every tech with jobs"} aria-label={tr("Email schedule")}
            >
              <Mail className={`w-4 h-4 ${emailingDay ? "animate-pulse" : ""}`} />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={`${btn("write")} h-8 w-8 justify-center !px-0`} disabled={autoScheduling} title={autoScheduling ? "Scheduling…" : "Auto-dispatch open work orders"} aria-label="Auto-dispatch" data-testid="btn-auto-dispatch">
                  <Zap className={`w-4 h-4 ${autoScheduling ? "animate-pulse" : ""}`} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleAutoSchedule("week")} data-testid="auto-dispatch-week">{tr("This Week")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleAutoSchedule("month")} data-testid="auto-dispatch-month">{tr("This Month")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {lastBatchId && (
              <button type="button" className={btn("nav")} onClick={handleUndoAutoSchedule} data-testid="btn-undo-auto-dispatch">{tr("Undo")}</button>
            )}
            <button type="button" className={`${btn("nav")} h-8 w-8 justify-center !px-0 border-red-300 text-red-600 hover:bg-red-50`} onClick={() => setClearConfirm(true)} title={tr("Clear this week's schedule")} aria-label={tr("Clear schedule")} data-testid="btn-clear-schedule">
              <Eraser className="w-4 h-4" />
            </button>
          </div>
          <div className="ml-auto flex items-center gap-1 shrink-0" data-testid="dispatch-toolbar-actions">
            <button type="button" className={`${btn("nav")} h-8 w-8 justify-center !px-0`} onClick={prev} data-testid="btn-prev" title={tr("Previous")}><ChevronLeft className="w-4 h-4" /></button>
            <button type="button" className={`${btn("nav")} h-8 !px-2`} onClick={goToday} data-testid="btn-today">{tr("Today")}</button>
            <button type="button" className={`${btn("nav")} h-8 w-8 justify-center !px-0`} onClick={next} data-testid="btn-next" title={tr("Next")}><ChevronRight className="w-4 h-4" /></button>
            <span style={{ "--datejump-minw": "0px" }}><DateJump label={dateLabel} value={currentDate} onPick={setCurrentDate} testId="dispatch-date-jump" /></span>
          {(() => {
            const { booked, available } = hoursSummary;
            const pct = available ? booked / available : 0;
            const tone = pct > 1 ? "text-rose-600" : pct >= 0.85 ? "text-emerald-600" : pct >= 0.5 ? "text-amber-600" : "text-slate-500";
            return (
              <div className="shrink-0 px-1 text-[11px] text-slate-500 num whitespace-nowrap" data-testid="dispatch-hours-summary"
                title={`${Math.round(pct * 100)}% booked — hours dispatched on the days shown vs. capacity: ${activeTechs.length} techs × Mon–Thu days not off × ${HOURS_PER_DAY}h`}>
                <span className="hidden 2xl:inline">{tr("Booked")} </span><span className={`font-semibold ${tone}`} data-testid="dispatch-hours-booked">{booked}</span>
                <span className="text-slate-400">/</span><span data-testid="dispatch-hours-available">{available}</span> hrs
              </div>
            );
          })()}
          </div>
        </div>
        {/* Full-page toggle — a bare arrow tucked above the toolbar's right edge, out of the button row. */}
        <button type="button" onClick={() => setFullPage(f => !f)}
          className={`absolute p-0.5 text-slate-400 hover:text-slate-800 transition-colors ${fullPage ? "right-3 top-1" : "right-0 -top-2"}`}
          title={fullPage ? "Exit full page (Esc)" : "Expand the board to the full page"} aria-label={fullPage ? "Exit full page" : "Full page"} data-testid="btn-dispatch-fullpage">
          {fullPage ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
        </button>

        <div className="data-table-container flex gap-1.5 p-3" style={{ height: fullPage ? "calc(100vh - 90px)" : "calc(100vh - 160px)" }}>
          <QueueSidebar
            width={queueWidth}
            userKey={userKey}
            queueCats={queueCats}
            queueSort={queueSort}
            globalSearch={searchQuery}
            queue={queue}
            approvedQueue={approvedQueue}
            contractJobs={contractJobs}
            completedContractJobs={completedContractJobs}
            onCompleteContractJob={completeContractJob}
            onReopenContractJob={reopenJob}
            onNewContractJob={() => setContractModal({ then: reloadContractJobs })}
            onEditContractJob={(job) => setContractModal({ job, then: reloadContractJobs })}
            dragWO={dragWO}
            setDragWO={setDragWO}
            smartSuggestions={smartSuggestions}
            loadSmartSuggest={loadSmartSuggest}
            onOpenWO={handleOpenWO}
            teamMode={teamMode}
            setTeamMode={setTeamMode}
            technicians={technicians}
            selectedKeys={selectedKeys}
            onDropSelected={handleBulkSendToQueue}
            onWOMaterialAdded={(woId, material) => {
              // Inject into queue + dispatches.parts so the new material
              // appears immediately on every card of this WO without a
              // full reload. Backend already persists it.
              setQueue(prev => prev.map(w => w.id === woId
                ? { ...w, parts: [...(w.parts || []), { description: material.description, quantity: material.quantity, part_number: material.part_number || "" }] }
                : w));
              setDispatches(prev => prev.map(d => d.wo_id === woId && !d.parts_override
                ? { ...d, parts: [...(d.parts || []), { description: material.description, quantity: material.quantity, part_number: material.part_number || "" }] }
                : d));
            }}
            onWOFieldNoteAdded={(woId, note) => {
              setQueue(prev => prev.map(w => w.id === woId ? { ...w, field_notes: [...(w.field_notes || []), note] } : w));
              setDispatches(prev => prev.map(d => d.wo_id === woId ? { ...d, field_notes: [...(d.field_notes || []), note] } : d));
            }}
          />
          <div role="separator" aria-orientation="vertical" onMouseDown={startQueueResize} onDoubleClick={() => { setQueueWidth(224); persistQueueWidth(224); }}
            className="w-1.5 shrink-0 cursor-col-resize rounded-full bg-slate-100 hover:bg-blue-300 active:bg-blue-400 transition-colors self-stretch"
            title="Drag to resize the work order pane · double-click to reset" data-testid="queue-resize-handle" />
          <div className="flex-1 overflow-auto min-w-0">
            {view === "day" ? (
              /* iter400 — Day board: today only, one extra-wide column, zoomed for readability */
              <div style={{ zoom: 1.4 }} data-testid="day-board">
                <WeekView weekDays={dayDays} technicians={activeTechs} getDispatchesForCell={getFilteredDispatchesForCell}
                  liveByTech={liveByTech}
                  onReorderStops={handleReorderStops}
                  getTechDayHours={getTechDayHours} dragWO={dragWO} dragDispatch={dragDispatch} setDragDispatch={setDragDispatch}
                  onDrop={handleDrop} onMoveDispatch={handleMoveDispatch}
                  onToggleGps={handleToggleTechGps}
                  doesTechMatchWO={doesTechMatchWO} hoverCell={hoverCell} setHoverCell={setHoverCell}
                  isTechOff={isTechOff} smartSuggestions={smartSuggestions} matchedTechIds={matchedTechIds}
                  categories={dispatchCategories}
                  flashTech={flashTech}
                  onOpenTimeOff={openTimeOff}
                  findTimeOffBlock={findTimeOffBlock}
                  findTimeOffBlocks={findTimeOffBlocks}
                  offDrag={offDrag}
                  startOffDrag={startOffDrag}
                  extendDrag={extendDrag}
                  startExtendDrag={startExtendDrag}
                  selectedKeys={selectedKeys}
                  isSelected={isSelected}
                  onToggleSelect={handleToggleSelect}
                  onOpenWO={(d) => handleOpenWO({ id: d.wo_id, client_id: d.client_id })}
                  onDispatchClick={(d, e) => setDispatchPopover({ dispatch: d, x: e.clientX, y: e.clientY })} />
              </div>
            ) : view === "week" ? (
              <WeekView weekDays={weekDays} technicians={activeTechs} getDispatchesForCell={getFilteredDispatchesForCell}
                liveByTech={liveByTech}
                onReorderStops={handleReorderStops}
                getTechDayHours={getTechDayHours} dragWO={dragWO} dragDispatch={dragDispatch} setDragDispatch={setDragDispatch}
                onDrop={handleDrop} onMoveDispatch={handleMoveDispatch}
                onToggleGps={handleToggleTechGps}
                doesTechMatchWO={doesTechMatchWO} hoverCell={hoverCell} setHoverCell={setHoverCell}
                isTechOff={isTechOff} smartSuggestions={smartSuggestions} matchedTechIds={matchedTechIds}
                categories={dispatchCategories}
                flashTech={flashTech}
                onOpenTimeOff={openTimeOff}
                findTimeOffBlock={findTimeOffBlock}
                findTimeOffBlocks={findTimeOffBlocks}
                offDrag={offDrag}
                startOffDrag={startOffDrag}
                extendDrag={extendDrag}
                startExtendDrag={startExtendDrag}
                selectedKeys={selectedKeys}
                isSelected={isSelected}
                onToggleSelect={handleToggleSelect}
                onOpenWO={(d) => handleOpenWO({ id: d.wo_id, client_id: d.client_id })}
                onDispatchClick={(d, e) => setDispatchPopover({ dispatch: d, x: e.clientX, y: e.clientY })} />
            ) : (
              <MonthView monthWeeks={monthWeeks} currentDate={currentDate} technicians={activeTechs}
                dispatches={filteredDispatches} dragWO={dragWO} dragDispatch={dragDispatch} setDragDispatch={setDragDispatch}
                onDrop={handleDrop} onMoveDispatch={handleMoveDispatch} onRemove={handleRemoveDispatch} onEdit={setEditModal}
                onDuplicate={handleDuplicateDispatch}
                doesTechMatchWO={doesTechMatchWO} hoverCell={hoverCell} setHoverCell={setHoverCell} isTechOff={isTechBlocked}
                onRequestTechPicker={(wo, day, e) => setMonthDropPicker({ wo, day, x: e.clientX, y: e.clientY })}
                onDispatchClick={(d, e) => setDispatchPopover({ dispatch: d, x: e.clientX, y: e.clientY })}
                showWeekends={showWeekends} />
            )}
          </div>
        </div>
      {/* Dispatch Edit Modal */}
      {editModal && <DispatchEditModal dispatch={editModal} onSave={handleSaveDispatchEdit} onClose={() => setEditModal(null)} />}

      {/* Multi-select action toolbar — appears bottom-center when one or
          more cards/blocks are selected. Pure floating UI, doesn't shift
          page layout. Shift+click for range, Cmd/Ctrl+click for additive,
          Esc to clear, Delete/Backspace for bulk delete. */}
      {selectedKeys.size > 0 && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-full bg-slate-900/95 text-white shadow-2xl border border-slate-700 px-3 py-1.5 text-sm"
          data-testid="multi-select-toolbar"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <span className="font-semibold tabular-nums" data-testid="multi-select-count">{selectedKeys.size} selected</span>
          <button
            type="button"
            onClick={handleBulkSendToQueue}
            className="px-3 py-1 rounded-full bg-blue-500 hover:bg-blue-400 transition-colors text-xs font-semibold"
            title="Real dispatches return to Open Work Orders. Time-off blocks just delete (not real WOs)."
            data-testid="multi-select-send-back"
          >
            Send to Open WOs
          </button>
          <button
            type="button"
            onClick={handleBulkDelete}
            className="px-3 py-1 rounded-full bg-red-500 hover:bg-red-400 transition-colors text-xs font-semibold"
            data-testid="multi-select-delete"
          >
            {tr("Delete")}
          </button>
          <button
            type="button"
            onClick={clearSelection}
            className="px-2 py-1 rounded-full text-slate-300 hover:text-white text-xs"
            data-testid="multi-select-cancel"
          >
            {tr("Cancel")}
          </button>
          <span className="hidden md:inline text-[10px] text-slate-400 pl-1 border-l border-slate-700 ml-1">
            shift / ⌘ to multi-select · del to remove · esc to clear
          </span>
        </div>
      )}

      {/* Time-off quick-pick menu — opens on cell double-click. */}
      {timeOffMenu && (
        <TimeOffMenu
          tech={timeOffMenu.tech}
          dateStr={timeOffMenu.dateStr}
          anchorRect={timeOffMenu.anchorRect}
          existingBlock={timeOffMenu.existingBlock}
          dayBlocks={timeOffMenu.dayBlocks || []}
          onClose={() => setTimeOffMenu(null)}
          onSaved={() => loadData()}
          onAddJob={(kind) => setAddStop({ techId: timeOffMenu.tech.id, date: timeOffMenu.dateStr, kind })}
          onRequestRange={(reason) => {
            // Escalate to the full modal for multi-day ranges.
            setTimeOffModal({
              tech: timeOffMenu.tech,
              initialDate: timeOffMenu.dateStr,
              blockId: timeOffMenu.existingBlock?.id || null,
              defaultReason: reason || timeOffMenu.existingBlock?.reason,
            });
          }}
        />
      )}
      {/* Time-off modal — multi-day editor escalated from the quick menu. */}
      {timeOffModal && (
        <TimeOffModal
          open={true}
          onOpenChange={(o) => { if (!o) setTimeOffModal(null); }}
          tech={timeOffModal.tech}
          initialDate={timeOffModal.initialDate}
          blockId={timeOffModal.blockId}
          defaultReason={timeOffModal.defaultReason}
          existingDispatchDates={(() => {
            const s = new Set();
            dispatches
              .filter(d => d.tech_id === timeOffModal.tech.id)
              .forEach(d => { if (d.date) s.add(d.date); });
            return s;
          })()}
          onSaved={() => loadData()}
        />
      )}
      {/* Field Notes Modal */}
      {notesModal && <FieldNotesModal data={notesModal} onAddNote={handleAddNote} onClose={() => setNotesModal(null)} />}
      {/* Month Drop Tech Picker */}
      {monthDropPicker && (
        <MonthDropTechPicker
          techs={activeTechs}
          wo={monthDropPicker.wo}
          day={monthDropPicker.day}
          x={monthDropPicker.x}
          y={monthDropPicker.y}
          onSelect={(techId) => { handleDrop(monthDropPicker.wo, techId, monthDropPicker.day); setMonthDropPicker(null); }}
          onClose={() => setMonthDropPicker(null)}
          isTechOff={isTechBlocked}
        />
      )}
      {/* Dispatch Popover — full detail + edit (used in both week and month views) */}
      <ContractJobModal
        open={!!contractModal}
        onOpenChange={(o) => { if (!o) setContractModal(null); }}
        job={contractModal?.job}
        kind={contractModal?.kind}
        onSaved={(saved) => { contractModal?.then?.(saved); reloadContractJobs(); refreshBoard().catch(() => {}); }}
        onComplete={async (job) => { await completeContractJob(job); setContractModal(null); }}
        onReopen={async (job) => { await reopenJob(job); setContractModal(null); }}
      />
      <AddStopPopup
        open={!!addStop}
        onOpenChange={(o) => { if (!o) setAddStop(null); }}
        techName={(technicians.find(t => t.id === addStop?.techId) || {}).name || "Technician"}
        date={addStop?.date}
        queue={queue}
        approvedQueue={approvedQueue}
        excludeWoIds={dispatches.filter(d => d.tech_id === addStop?.techId && d.date === addStop?.date && d.wo_id).map(d => d.wo_id)}
        excludeContractIds={dispatches.filter(d => d.tech_id === addStop?.techId && d.date === addStop?.date && d.contract_job_id).map(d => d.contract_job_id)}
        onPick={handleAddStopPick}
        kind={addStop?.kind}
        onNewContractJob={(endDate, newKind) => { const ctx = addStop; setAddStop(null); setContractModal({ kind: newKind, then: (saved) => { if (saved?.id) handleContractDrop({ ...saved, job_kind: saved.job_kind || "contract" }, ctx.techId, new Date(`${ctx.date}T12:00:00`), endDate ? new Date(`${endDate}T12:00:00`) : null); } }); }}
      />
      {dispatchPopover && (
        <DispatchPopover
          dispatch={dispatchPopover.dispatch}
          x={dispatchPopover.x}
          y={dispatchPopover.y}
          technicians={activeTechs}
          onClose={() => setDispatchPopover(null)}
          onAddStop={(d) => { setDispatchPopover(null); setAddStop({ techId: d.tech_id, date: d.date }); }}
          onEditContractJob={(d) => { setDispatchPopover(null); const job = allContractJobs.find(j => j.id === d.contract_job_id); setContractModal({ job: job || { id: d.contract_job_id, name: d.wo_title }, then: reloadContractJobs }); }}
          onRemove={(id) => { setDispatchPopover(null); handleRemoveDispatch(id); }}
          onDuplicate={(d, date, techId) => { handleDuplicateDispatch(d, date, techId); }}
          onNotes={(d) => { setDispatchPopover(null); openNotesModal(d); }}
          getNextWeekday={getNextWeekday}
          onOpenWO={(d) => { setDispatchPopover(null); handleOpenWO({ id: d.wo_id, client_id: d.client_id }); }}
          onComplete={(d) => { setDispatchPopover(null); handleCompleteWO(d); }}
          onAddDispatchNote={async (d, text) => {
            try {
              const res = await addDispatchNote(d.id, text, localStorage.getItem("abs_current_user") || "Dispatch");
              const saved = res?.data;
              setDispatches(prev => prev.map(x => x.id === d.id
                ? { ...x, notes: [...(Array.isArray(x.notes) ? x.notes : []), saved] }
                : x));
              // Also patch the open popover so the new note shows immediately.
              setDispatchPopover(prev => prev?.dispatch?.id === d.id
                ? { ...prev, dispatch: { ...prev.dispatch, notes: [...(Array.isArray(prev.dispatch.notes) ? prev.dispatch.notes : []), saved] } }
                : prev);
              toast.success("Note added");
            } catch {
              toast.error("Failed to save dispatch note");
            }
          }}
          onAddMaterial={async (d, material) => {
            // Append to dispatch.parts_override so the tech card immediately
            // sees the new part. Reads existing override (or starts from
            // the current parts list) so no work is lost.
            try {
              const existing = Array.isArray(d.parts_override) && d.parts_override.length > 0
                ? d.parts_override
                : (Array.isArray(d.parts) ? d.parts : []);
              const next = [...existing, material];
              const res = await updateDispatch(d.id, { parts_override: next });
              const updated = res?.data || { ...d, parts_override: next, parts: next };
              setDispatches(prev => prev.map(x => x.id === d.id ? { ...x, ...updated, parts: next } : x));
              setDispatchPopover(prev => prev?.dispatch?.id === d.id
                ? { ...prev, dispatch: { ...prev.dispatch, ...updated, parts: next } }
                : prev);
              toast.success("Material added");
            } catch {
              toast.error("Failed to add material");
            }
          }}
          onAddFieldNote={async (d, text) => {
            // Append a tech-style field note from inside the popover.
            // Tech name pulls from the self-identify localStorage so the
            // attribution matches whoever's logged in (falls back to
            // "Dispatch" for unidentified sessions). Field notes live on
            // the WO so they carry forward across every dispatch of the
            // same project.
            try {
              const techName = localStorage.getItem("abs_current_user") || "Dispatch";
              if (isContract(d)) {
                const res = await addContractFieldNote(d.contract_job_id, { text, tech_name: techName });
                const saved = res?.data;
                const append = (notes) => [...(Array.isArray(notes) ? notes : []), saved];
                setDispatches(prev => prev.map(x => x.contract_job_id === d.contract_job_id ? { ...x, field_notes: append(x.field_notes) } : x));
                setDispatchPopover(prev => prev?.dispatch?.contract_job_id === d.contract_job_id
                  ? { ...prev, dispatch: { ...prev.dispatch, field_notes: append(prev.dispatch.field_notes) } }
                  : prev);
                toast.success("Field note added");
                return;
              }
              const res = await addFieldNote(d.wo_id, { text, tech_name: techName });
              const saved = res?.data || { id: `tmp-${Date.now()}`, text, tech_name: techName, created_at: new Date().toISOString() };
              const append = (notes) => [...(Array.isArray(notes) ? notes : []), saved];
              setDispatches(prev => prev.map(x => x.wo_id === d.wo_id ? { ...x, field_notes: append(x.field_notes) } : x));
              setDispatchPopover(prev => prev?.dispatch?.wo_id === d.wo_id
                ? { ...prev, dispatch: { ...prev.dispatch, field_notes: append(prev.dispatch.field_notes) } }
                : prev);
              toast.success("Field note added");
            } catch {
              toast.error("Failed to save field note");
            }
          }}
        />
      )}
      {/* Auto-Fill Prompt */}
      {autoFillPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setAutoFillPrompt(null)} data-testid="auto-fill-prompt">
          <div className="bg-white rounded-lg shadow-xl p-5 w-80" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-slate-800 mb-2">{tr("Auto-fill remaining hours?")}</h3>
            <p className="text-xs text-slate-600 mb-1">
              <span className="font-semibold">{autoFillPrompt.wo.wo_number}</span> — {autoFillPrompt.wo.title}
            </p>
            <p className="text-xs text-slate-500 mb-3">
              {autoFillPrompt.wo.remaining_hours}h remaining. Fill consecutive weekdays with the same tech until complete?
            </p>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setAutoFillPrompt(null)} data-testid="auto-fill-no">{tr("No, just today")}</Button>
              <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={handleAutoFill} data-testid="auto-fill-yes">{tr("Auto-fill all hours")}</Button>
            </div>
          </div>
        </div>
      )}
      {/* Clear Schedule Confirmation */}
      {clearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setClearConfirm(false)} data-testid="clear-schedule-confirm">
          <div className="bg-white rounded-lg shadow-xl p-5 w-80" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-red-700 mb-2">{tr("Clear Future Schedule?")}</h3>
            <p className="text-xs text-slate-600 mb-3">
              This will remove <span className="font-bold">all dispatches after today</span> in the current {view} view. Past and today's dispatches are preserved.
            </p>
            <p className="text-xs text-red-500 font-medium mb-3">This action cannot be undone. Are you sure?</p>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setClearConfirm(false)} data-testid="clear-cancel">{tr("Cancel")}</Button>
              <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={handleClearSchedule} data-testid="clear-confirm">{tr("Clear Schedule")}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </SearchHiContext.Provider>
  );
}


/* Queue Sidebar */
// iter398 — pane state (which groups are open, which cards are expanded) sticks per browser.
const paneKey = (userKey) => `abs.dispatch.pane.${userKey}`;
const readPane = (userKey) => { try { return JSON.parse(localStorage.getItem(paneKey(userKey)) || localStorage.getItem("abs.dispatch.pane") || "{}"); } catch { return {}; } };
const writePane = (userKey, patch) => { try { localStorage.setItem(paneKey(userKey), JSON.stringify({ ...readPane(userKey), ...patch })); } catch { /* private mode */ } };

function GroupPill({ on, onClick, title, testId, children }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on} title={title} data-testid={testId}
      className={`inline-flex h-6 min-w-[36px] items-center justify-center rounded-full px-2.5 text-[10px] font-bold tracking-wide transition-colors ${on ? "seg-on" : "seg-off"}`}>
      {children}
    </button>
  );
}

function QueueSidebar({ queue, approvedQueue, contractJobs = [], completedContractJobs = [], onCompleteContractJob, onReopenContractJob, onNewContractJob, onEditContractJob, dragWO, setDragWO, smartSuggestions, loadSmartSuggest, onOpenWO, teamMode, setTeamMode, technicians, onWOMaterialAdded, onWOFieldNoteAdded, selectedKeys, onDropSelected, globalSearch = "", queueCats = [], queueSort = "scheduled", width = 224, userKey = "anon" }) {
  const saved = useMemo(() => readPane(userKey), [userKey]);
  const [groups, setGroupsState] = useState(() => ({ open: true, approved: false, cow: true, done: false, ...(saved.groups || {}) }));
  const setGroup = (k, v) => setGroupsState(g => { const next = { ...g, [k]: typeof v === "boolean" ? v : !g[k] }; writePane(userKey, { groups: next }); return next; });
  const allOpen = groups.open && groups.approved && groups.cow && groups.done;
  const setAllGroups = (on) => setGroupsState(() => { const next = { open: on, approved: on, cow: on, done: on }; writePane(userKey, { groups: next }); return next; });
  const approvedOpen = groups.approved, contractOpen = groups.cow;
  const setApprovedOpen = (fn) => setGroup("approved", typeof fn === "function" ? fn(groups.approved) : fn);
  const setContractOpen = (fn) => setGroup("cow", typeof fn === "function" ? fn(groups.cow) : fn);
  // One search — the toolbar's — drives the whole pane (open, approved, COW, completed).
  const matchesQuery = (wo) => deepMatch(wo, globalSearch);
  const sortQueue = (rows) => {
    const cmp = {
      scheduled: (a, b) => (a.effective_date || a.scheduled_date || "9999").localeCompare(b.effective_date || b.scheduled_date || "9999"),
      wo_number: (a, b) => (a.wo_number || "").localeCompare(b.wo_number || ""),
      customer: (a, b) => (a.client_name || "").localeCompare(b.client_name || ""),
      site: (a, b) => `${a.site_name || ""} ${a.site_city || ""}`.localeCompare(`${b.site_name || ""} ${b.site_city || ""}`),
      oldest: (a, b) => (a.created_at || a.approved_at || "9999").localeCompare(b.created_at || b.approved_at || "9999"),
      hours: (a, b) => (b.remaining_hours ?? b.labor_hours_budget ?? 0) - (a.remaining_hours ?? a.labor_hours_budget ?? 0),
    }[queueSort] || (() => 0);
    return [...rows].sort(cmp);
  };
  const keep = (wo) => matchesQuery(wo) && matchesCategories(wo, queueCats);
  const visibleQueue = sortQueue(queue.filter(keep));
  const visibleApproved = sortQueue(approvedQueue.filter(keep));
  const hiddenCount = (queue.length - visibleQueue.length) + (approvedQueue.length - visibleApproved.length);
  // Per-card expanded state — keyed by wo.id; sticky per browser.
  const [expandedWOs, setExpandedWOsState] = useState(() => saved.expanded || {});
  const setExpandedWOs = (fn) => setExpandedWOsState(prev => { const next = typeof fn === "function" ? fn(prev) : fn; writePane(userKey, { expanded: next }); return next; });
  const toggleExpanded = (id) => setExpandedWOs(prev => ({ ...prev, [id]: !prev[id] }));
  const allExpanded = visibleQueue.length > 0 && visibleQueue.every(w => expandedWOs[w.id]);
  const expandAll = (on) => setExpandedWOs(prev => ({ ...prev, ...Object.fromEntries(visibleQueue.map(w => [w.id, on])) }));
  // Per-card Smart Dispatch toggle. Clicking the "Smart Dispatch" button
  // loads the suggestions (if missing) AND opens the panel; clicking
  // again collapses it without dropping the cached results.
  const [smartOpen, setSmartOpen] = useState({});
  // When the user has cards selected, the sidebar acts as a drop zone:
  // dragging any selected card here returns real dispatches to Open WOs
  // (and silently deletes time-off blocks since they aren't real WOs).
  const hasSelection = (selectedKeys?.size || 0) > 0;
  const [dropHover, setDropHover] = useState(false);
  return (
    <div
      className={`shrink-0 flex flex-col rounded-lg overflow-y-auto pr-0.5 ${hasSelection && dropHover ? "ring-2 ring-blue-500 bg-blue-50/40" : ""}`}
      style={{ width }}
      data-testid="dispatch-queue"
      onDragOver={(e) => { if (hasSelection) { e.preventDefault(); setDropHover(true); } }}
      onDragLeave={() => setDropHover(false)}
      onDrop={(e) => {
        if (!hasSelection) return;
        e.preventDefault(); setDropHover(false);
        onDropSelected?.();
      }}
    >
      {/* iter398/399 — quick groups: O (open) · A (approved) · COW (contract/OH/warranty) · ✓ (completed).
          Chevrons at the left open / close every group at once. */}
      <div className="relative flex items-center justify-center gap-1 mb-2 h-7" data-testid="queue-group-pills">
        <button type="button" onClick={() => setAllGroups(!allOpen)} data-testid="queue-expand-all"
          className="absolute left-0 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-400 hover:text-slate-800 hover:bg-slate-100"
          title={allOpen ? "Collapse all groups" : "Expand all groups"}>
          {allOpen ? <ChevronsDownUp className="w-3.5 h-3.5" /> : <ChevronsUpDown className="w-3.5 h-3.5" />}
        </button>
        <GroupPill on={groups.open} onClick={() => setGroup("open")} title={tr("Open work orders")} testId="group-pill-open">O</GroupPill>
        <GroupPill on={groups.approved} onClick={() => setGroup("approved")} title={tr("Approved, not yet scheduled")} testId="group-pill-approved">A</GroupPill>
        <GroupPill on={groups.cow} onClick={() => setGroup("cow")} title={tr("Contract · Overhead · Warranty")} testId="group-pill-cow">COW</GroupPill>
        <GroupPill on={groups.done} onClick={() => setGroup("done")} title={tr("Completed contract jobs")} testId="group-pill-done"><CheckCircle2 className="w-3.5 h-3.5" /></GroupPill>
        {hiddenCount > 0 && (globalSearch || queueCats.length > 0) && (
          <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[9px] text-slate-400 num whitespace-nowrap" data-testid="queue-search-hidden">{hiddenCount} hidden</span>
        )}
      </div>
      <div className="text-xs font-semibold text-slate-600 px-1 mb-1.5 flex items-center gap-1">
        <button type="button" onClick={() => setGroup("open")} className="flex items-center gap-1 group" data-testid="open-queue-toggle">
          <span className="group-hover:text-slate-800">{tr("Open Work Orders")}</span>
          {groups.open ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
        </button>
        {groups.open && visibleQueue.length > 0 && (
          <button type="button" onClick={() => expandAll(!allExpanded)} className="text-slate-400 hover:text-slate-700" title={allExpanded ? "Collapse cards" : "Expand cards"} data-testid="queue-expand-cards">
            {allExpanded ? <ChevronsDownUp className="w-3 h-3" /> : <ChevronsUpDown className="w-3 h-3" />}
          </button>
        )}
        {hasSelection ? (
          <span className="ml-auto text-[10px] text-brand font-semibold whitespace-nowrap" data-testid="queue-drop-hint">drop here</span>
        ) : (
          <span className="ml-auto text-[11px] font-normal text-slate-500 num" data-testid="queue-count">{visibleQueue.length}{visibleQueue.length !== queue.length ? ` of ${queue.length}` : ""}</span>
        )}
      </div>
      <div className={`${groups.open ? "" : "hidden"} space-y-1.5 pr-1`} data-testid="open-queue-list">
        {visibleQueue.length === 0 && (
          <div className="text-xs text-slate-400 text-center py-4">
            {queue.length === 0 ? "No open work orders" : "Nothing matches that search or filter"}
          </div>
        )}
        {visibleQueue.map(wo => {
          const overBudget = wo.dispatched_hours > wo.labor_hours_budget && wo.labor_hours_budget > 0;
          const pct = wo.labor_hours_budget > 0 ? Math.round((wo.dispatched_hours / wo.labor_hours_budget) * 100) : 0;
          const suggestions = smartSuggestions[wo.id];
          return (
            <div key={wo.id} draggable onDragStart={() => setDragWO(wo)} onDragEnd={() => setDragWO(null)}
              onClick={() => toggleExpanded(wo.id)}
              className={`bg-white border rounded-lg cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all ${dragWO?.id === wo.id ? "border-blue-400 shadow-md ring-2 ring-blue-100" : "border-slate-200"}`}
              data-testid={`queue-wo-${wo.id}`}>
              <div className="p-1.5">
                {/* Title — wo# header. Title is the eye-catcher (project
                    name), wo# is the secondary identifier. Both are
                    clickable to open the WO. Removed drag-handle dots
                    since the whole card is draggable; saves a row. */}
                <div className="flex items-baseline gap-1 mb-0.5 min-w-0">
                  <button onClick={(e) => { e.stopPropagation(); onOpenWO(wo); }}
                    className="text-[10px] font-semibold text-slate-800 hover:text-blue-700 hover:underline flex-1 min-w-0 text-left break-words leading-tight"
                    data-testid={`queue-wo-title-${wo.id}`}
                    title={tr("Open Work Order")}>{wo.title || "Untitled"}</button>
                  <button onClick={(e) => { e.stopPropagation(); onOpenWO(wo); }}
                    className="text-[9px] font-bold text-blue-700 hover:underline shrink-0"
                    data-testid={`queue-wo-link-${wo.id}`}
                    title={tr("Open Work Order")}>{wo.wo_number}</button>
                  <button onClick={(e) => { e.stopPropagation(); toggleExpanded(wo.id); }}
                    className="text-slate-400 hover:text-slate-600 shrink-0"
                    data-testid={`queue-expand-${wo.id}`}
                    title={expandedWOs[wo.id] ? "Show less" : "Show more"}>
                    {expandedWOs[wo.id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  </button>
                </div>

                {/* Status chips row — budget + CO + Team toggle. Drag
                    handle dots removed (the entire card is draggable). */}
                <div className="flex items-center gap-1 mb-0.5 flex-wrap">
                  <Badge variant="outline" className={`text-[8px] px-1 py-0 ${pct > 100 ? "bg-red-50 text-red-600 border-red-200" : pct >= 75 ? "bg-yellow-50 text-yellow-700 border-yellow-200" : "bg-green-50 text-green-600 border-green-200"}`}>
                    {wo.remaining_hours > 0 ? `${wo.remaining_hours}h left` : overBudget ? "Over budget" : "Dispatched"}
                  </Badge>
                  {(wo.co_hours_breakdown || []).map(co => (
                    <Badge key={co.co_id} variant="outline"
                      className="text-[8px] px-1 py-0 bg-purple-50 text-purple-700 border-purple-200 font-semibold"
                      data-testid={`queue-co-${wo.id}-${co.co_id}`}
                      title={co.title || co.co_number}>
                      {co.co_number} · {co.hours}h
                    </Badge>
                  ))}
                  {(() => {
                    const assignedTechs = (wo.assigned_tech_ids || []).map(id => technicians.find(t => t.id === id)).filter(Boolean);
                    const hasTeammate = assignedTechs.some(t => t.teammate_id);
                    if (!hasTeammate) return null;
                    return (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setTeamMode(prev => ({ ...prev, [wo.id]: !prev[wo.id] })); }}
                        className={`text-[8px] px-1.5 py-0 rounded-full border transition-all ${teamMode[wo.id] ? "bg-indigo-100 text-indigo-700 border-indigo-300 font-bold" : "bg-slate-50 text-slate-400 border-slate-200"}`}
                        data-testid={`team-toggle-${wo.id}`} title="Toggle team dispatch — auto-dispatch teammate">
                        {tr("Team")}
                      </button>
                    );
                  })()}
                </div>

                {/* Core info — customer · site, address, site contact,
                    materials, lift required, hours bar. Mirrors the
                    tech card and dispatch board cell layout 1:1. */}
                <div className="text-[10px] text-slate-500 font-medium break-words">{wo.client_name}{wo.site_name ? ` · ${wo.site_name}` : ""}</div>
                {wo.latest_note && (
                  <div className="flex items-start gap-1 text-[9px] text-amber-800 bg-amber-50 border border-amber-100 rounded px-1 py-0.5 mt-0.5"
                       title={`${wo.latest_note.text}\n— ${wo.latest_note.by}`}
                       data-testid={`queue-note-preview-${wo.id}`}>
                    <span className="break-words min-w-0 line-clamp-2">{wo.latest_note.text}</span>
                  </div>
                )}
                {wo.site_address ? (
                  <div className="text-[9px] text-slate-400 mt-0.5 break-words">{wo.site_address}</div>
                ) : (
                  <div className="text-[9px] text-slate-400 italic mt-0.5">{tr("Pending site")}</div>
                )}
                {wo.contact_name && (
                  <div className="flex items-center gap-1 text-[9px] mt-0.5 flex-wrap">
                    {wo.contact_email ? (
                      <a href={`mailto:${wo.contact_email}`} className="text-blue-600 hover:underline font-medium" onClick={(e) => e.stopPropagation()}>{wo.contact_name}</a>
                    ) : (
                      <span className="text-slate-600 font-medium">{wo.contact_name}</span>
                    )}
                    {wo.contact_phone && (
                      <a href={`tel:${wo.contact_phone}`} className="text-blue-600 hover:underline" onClick={(e) => e.stopPropagation()}>· {wo.contact_phone}</a>
                    )}
                  </div>
                )}
                {wo.parts?.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {wo.parts.map((p, i) => {
                      const done = isPartChecked(p, wo.dispatch_card_progress);
                      return (
                        // eslint-disable-next-line react/no-array-index-key -- parts list for dispatch card; check-state is tracked by idx in dispatch_card_progress
                        <div key={`pt-${wo.id}-${i}`} className={`text-[9px] font-semibold ${done ? "text-emerald-800 bg-emerald-50 rounded px-1 line-through" : "text-slate-700"}`} data-testid={`queue-part-${wo.id}-${i}`}>
                          <span className="break-words min-w-0">{Math.round(p.quantity)}x {p.description}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Lift requirement — plain text under materials. Same
                    pattern as the dispatch board card and tech /field
                    card so all 3 surfaces match. */}
                {(wo.lift_note || wo.lift_required != null) && (
                  <div className="text-[9px] text-slate-800 font-medium flex items-start gap-0.5 mt-0.5" data-testid={`queue-lift-${wo.id}`}>
                    <span className="break-words min-w-0">{wo.lift_note || (wo.lift_required ? "Lift Required" : "No lift required")}</span>
                  </div>
                )}
                {/* iter233 — DOT trailer inspection requirement. Prompted on
                    every dispatch card; default OFF. Flags the WO so the
                    tech's card + dashboard track the inspection. */}
                <label
                  className="flex items-center gap-1.5 mt-1 cursor-pointer select-none"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`queue-dot-toggle-${wo.id}`}
                >
                  <input
                    type="checkbox"
                    className="w-3 h-3 accent-amber-600"
                    defaultChecked={!!wo.dot_inspection_required}
                    onChange={async (e) => {
                      const req = e.target.checked;
                      try {
                        await setWODotRequired(wo.id, req);
                        toast.success(req ? "DOT inspection required — tech will be prompted" : "DOT inspection requirement removed");
                      } catch { toast.error("Could not update DOT flag"); }
                    }}
                  />
                  <span className="text-[9px] font-medium text-amber-700">{tr("DOT Inspection Required?")}</span>
                  <span className="text-[8px] text-slate-400">(trailer use)</span>
                </label>
                {/* Hours progress — always shown. Denominator is the WO's labor
                    budget (from line items when `labor_hours_budget` isn't set
                    explicitly). Numerator (`dispatched_hours`) sums tech-logged
                    actuals for completed-for-the-day dispatches and scheduled
                    hours for dispatches still on the board. */}
                {(() => {
                  const budget = wo.labor_hours_budget || wo.labor_hours || 0;
                  const used = wo.dispatched_hours || 0;
                  const localPct = budget > 0 ? Math.round((used / budget) * 100) : 0;
                  const barColor = localPct > 100 ? "#ef4444" : localPct >= 75 ? "#eab308" : "#22c55e";
                  return (
                    <div className="mt-1.5" data-testid={`queue-hours-${wo.id}`}>
                      <div className="flex justify-between text-[8px] mb-0.5" style={{ color: barColor }}>
                        <span>{used}{budget > 0 ? ` of ${budget}h` : "h used"}</span>
                        {budget > 0 ? <span>{localPct}%</span> : <span className="text-slate-400">no budget</span>}
                      </div>
                      <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: budget > 0 ? `${Math.min(localPct, 100)}%` : "0%", backgroundColor: barColor }} />
                      </div>
                    </div>
                  );
                })()}

                {/* Expanded section — scope chips, dispatcher add-material
                    inline form, dispatch + field notes with + Note buttons.
                    Lift note shown plain-text in collapsed view above
                    (under materials), so it's not duplicated here. */}
                {expandedWOs[wo.id] && (
                  <div className="mt-2 pt-1.5 border-t border-dashed border-slate-200 space-y-1" data-testid={`queue-expanded-${wo.id}`}>
                    {wo.wo_scopes?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {wo.wo_scopes.map(s => (<span key={s} className="text-[8px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{s}</span>))}
                      </div>
                    )}
                    <QueueMaterialAdder woId={wo.id} onAdded={onWOMaterialAdded} />
                    <QueueFieldNoteAdder woId={wo.id} onAdded={onWOFieldNoteAdded} />
                    {wo.field_notes?.length > 0 && (
                      <div className="space-y-0.5" data-testid={`queue-notes-${wo.id}`}>
                        <div className="text-[9px] font-semibold text-slate-600">{tr("Field Notes")}</div>
                        {wo.field_notes.slice(-3).map((n, i) => (
                          <div key={n.id || i} className="text-[8px] text-slate-500 italic flex items-start gap-1.5">
                            <div className="flex-1 min-w-0 break-words">
                              <span className="font-semibold text-slate-600 not-italic">{abbreviateName(n.created_by || n.tech_name)}:</span> <NoteText note={n} />
                            </div>
                            {n.created_at && <span className="not-italic text-slate-400 text-[7px] shrink-0">{formatNoteDate(n.created_at)}</span>}
                          </div>
                        ))}
                        {wo.field_notes.length > 3 && (
                          <div className="text-[7px] text-slate-400">+{wo.field_notes.length - 3} more notes</div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <button onClick={(e) => {
                  e.stopPropagation();
                  setSmartOpen(prev => ({ ...prev, [wo.id]: !prev[wo.id] }));
                  // Lazy-load on first open. After that the cached
                  // suggestions get reused so toggling is instant.
                  if (!smartSuggestions[wo.id]) loadSmartSuggest(wo.id);
                }}
                  className="flex items-center gap-1 text-[9px] text-amber-600 hover:text-amber-700 mt-1.5 font-medium"
                  data-testid={`smart-suggest-${wo.id}`}>
                  {tr("Smart Dispatch")}
                  {smartOpen[wo.id] ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
                </button>
                {smartOpen[wo.id] && suggestions?.length > 0 && (
                  <div className="mt-1 space-y-0.5 border-t border-slate-100 pt-1">
                    {suggestions.slice(0, 3).map((s, i) => (
                      <div key={s.tech_id} className="flex items-center gap-1 text-[9px] group relative">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.tech_color }} />
                        <span className={`font-medium ${i === 0 ? "text-green-700" : "text-slate-600"}`}>{s.tech_name}</span>
                        {s.is_helper && <span className="text-[7px] bg-amber-100 text-amber-700 px-1 rounded">helper</span>}
                        <span className={`ml-auto font-semibold ${s.total_score >= 70 ? "text-green-600" : s.total_score >= 40 ? "text-amber-600" : "text-red-500"}`}>{s.total_score}%</span>
                        <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50 bg-slate-800 text-white text-[8px] rounded px-2 py-1.5 whitespace-nowrap shadow-lg" data-testid={`smart-tooltip-${s.tech_id}`}>
                          <div className="font-bold mb-0.5">{s.is_helper ? "Helper" : "Score"}: {s.total_score}%</div>
                          <div>Skills Match: {s.scope_score}% <span className="text-slate-400">x40%</span></div>
                          <div>Day Availability: {s.workload_score}% <span className="text-slate-400">x30%</span></div>
                          <div>Proximity: {s.proximity_score}% <span className="text-slate-400">x20%</span></div>
                          <div>Week Balance: {s.week_score}% <span className="text-slate-400">x10%</span></div>
                          <div className="border-t border-slate-600 mt-0.5 pt-0.5 text-slate-400">{s.day_hours_used}h today · {s.week_hours_used}h/40h week</div>
                          {s.is_helper && <div className="text-amber-300 mt-0.5">Paired with experienced tech on this WO</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Approved Work Orders — collapsible bench of approved-but-not-yet-
          dispatched WOs. Click to expand; drag-to-schedule still works the
          same as the Open queue. Once a tech is dropped on the calendar
          the WO promotes to "scheduled" and migrates upstairs. */}
      <div className="mt-3 border-t border-slate-200 pt-2 shrink-0 flex flex-col" data-testid="approved-queue-panel">
        <button onClick={() => setApprovedOpen(o => !o)}
          className="flex items-center justify-between w-full px-1 mb-1.5 group"
          data-testid="approved-queue-toggle">
          <span className="text-xs font-semibold text-slate-600 group-hover:text-slate-800">
            {tr("Approved Work Orders")} <span className="font-normal text-slate-500 num">{visibleApproved.length}{visibleApproved.length !== approvedQueue.length ? ` of ${approvedQueue.length}` : ""}</span>
          </span>
          {approvedOpen
            ? <ChevronDown className="w-3 h-3 text-slate-400" />
            : <ChevronRight className="w-3 h-3 text-slate-400" />}
        </button>
        {approvedOpen && (
          <div className="space-y-1.5 pr-1" data-testid="approved-queue-list">
            {visibleApproved.length === 0 && (
              <div className="text-[10px] text-slate-400 italic text-center py-3">
                {approvedQueue.length === 0
                  ? "Nothing waiting — every approved WO has a dispatch on the calendar."
                  : "Nothing matches that search."}
              </div>
            )}
            {visibleApproved.map(wo => (
              <div key={wo.id} draggable onDragStart={() => setDragWO(wo)} onDragEnd={() => setDragWO(null)}
                className="bg-amber-50/60 border border-amber-200 rounded-lg p-2 cursor-grab active:cursor-grabbing hover:border-amber-400 hover:shadow-sm transition-all"
                data-testid={`approved-wo-${wo.id}`}>
                <div className="flex items-center gap-1 mb-0.5 flex-wrap">
                  <span className="text-[10px] font-bold text-amber-800">{wo.wo_number}</span>
                  <Badge variant="outline" className="text-[8px] px-1 py-0 bg-amber-100 text-amber-700 border-amber-300">
                    {tr("Needs scheduling")}
                  </Badge>
                  {wo.annual_service && <span className="text-[8px] text-violet-600 font-semibold">{tr("Annual")}</span>}
                </div>
                <div className="text-[10px] text-slate-500 font-medium break-words">
                  {wo.client_name}{wo.site_name ? ` · ${wo.site_name}` : ""}
                </div>
                {wo.latest_note && (
                  <div className="flex items-start gap-1 text-[9px] text-amber-800 bg-amber-100/70 border border-amber-200 rounded px-1 py-0.5 mt-0.5"
                       title={`${wo.latest_note.text}\n— ${wo.latest_note.by}`}
                       data-testid={`approved-note-preview-${wo.id}`}>
                    <span className="break-words min-w-0 line-clamp-2">{wo.latest_note.text}</span>
                  </div>
                )}
                <div className="text-[11px] font-medium text-amber-800 break-words leading-snug cursor-pointer hover:underline"
                  onClick={(e) => { e.stopPropagation(); onOpenWO(wo); }}
                  data-testid={`approved-wo-link-${wo.id}`}>
                  {wo.title || "Untitled"}
                </div>
                {wo.site_address && (
                  <div className="text-[9px] text-slate-400 mt-0.5 truncate">{wo.site_address}</div>
                )}
                <div className="flex items-center justify-between mt-1 text-[9px] text-slate-500">
                  <span>{wo.labor_hours_budget}h budget</span>
                  {wo.approved_at && <span className="text-slate-400">Approved {formatNoteDate(wo.approved_at)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contract jobs — Contract-division projects (no WO). Drag onto a tech/day like a WO. */}
      <div className="mt-3 border-t border-slate-200 pt-2 shrink-0 flex flex-col" data-testid="contract-jobs-panel">
        <div className="flex items-center justify-between px-1 mb-1.5">
          <button onClick={() => setContractOpen(o => !o)} className="flex items-center gap-1 group min-w-0 text-left" data-testid="contract-jobs-toggle">
            <span className="text-xs font-semibold text-slate-600 group-hover:text-slate-800 whitespace-nowrap">{tr("Contract · OH · Warranty")} <span className="font-normal text-slate-500 num">{contractJobs.filter(j => matchesQuery(j)).length}</span></span>
            {contractOpen ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
          </button>
          <button onClick={onNewContractJob} className="text-[10px] font-semibold text-blue-600 hover:underline whitespace-nowrap shrink-0" data-testid="new-contract-job-btn">+ {tr("New")}</button>
        </div>
        {contractOpen && (
          <div className="space-y-1.5 pr-1" data-testid="contract-jobs-list">
            {contractJobs.length === 0 && <div className="text-[10px] text-slate-400 italic text-center py-2">No contract jobs yet — click New.</div>}
            {contractJobs.filter(j => matchesQuery(j)).map(j => (
              <div key={j.id} draggable onDragStart={() => setDragWO({ ...j, job_kind: j.job_kind || "contract", title: j.name })} onDragEnd={() => setDragWO(null)}
                onClick={() => onEditContractJob?.(j)}
                className={`relative bg-white border rounded-lg cursor-grab hover:border-blue-400 hover:shadow-sm transition-all pl-2.5 pr-2 py-1.5 overflow-hidden ${dragWO?.id === j.id ? "border-blue-400 shadow-md ring-2 ring-blue-100" : "border-slate-200"}`}
                data-testid={`contract-job-card-${j.id}`} title={tr("Drag onto a tech's day · click to edit")}>
                <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: kindMeta(j.job_kind).color }} />
                <div className="flex items-center gap-1">
                  <JobTag kind={j.job_kind || "contract"} />
                  <span className="text-[10px] font-bold text-slate-800 truncate flex-1">{j.name}</span>
                  {j.dot_inspection_required && <span className="text-[7px] font-bold text-red-600 bg-red-50 border border-red-200 rounded px-1">DOT</span>}
                  <button type="button" onClick={(e) => { e.stopPropagation(); if (window.confirm(`Mark "${j.name}" complete?\n\nIt leaves this list; stops already on the schedule stay put.`)) onCompleteContractJob?.(j); }}
                    className="shrink-0 text-[9px] font-semibold text-slate-400 hover:text-emerald-600 transition-colors" title={tr("Mark complete — leaves this list")} data-testid={`contract-job-complete-${j.id}`}>
                    {tr("Done")}
                  </button>
                </div>
                <div className="text-[8px] text-slate-500 truncate mt-0.5">{j.site_address || tr("No address")}{j.geocode_status === "failed" ? " · not geocoded" : ""}</div>
                {j.contact_name && <div className="text-[8px] text-slate-500 truncate">{j.contact_name}{j.contact_phone ? ` · ${j.contact_phone}` : ""}</div>}
              </div>
            ))}
          </div>
        )}
        {completedContractJobs.length > 0 && (
          <div className="pt-1 pr-1" data-testid="completed-contract-jobs">
            <button type="button" onClick={() => setGroup("done")} className="w-full flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800 py-1 group" data-testid="completed-contract-jobs-toggle">
              <CheckCircle2 className="w-3 h-3 text-emerald-500" />{tr("Completed")} <span className="font-normal num">{completedContractJobs.filter(matchesQuery).length}</span>
              {groups.done ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
            </button>
            {groups.done && completedContractJobs.filter(matchesQuery).map(j => (
              <div key={j.id} className="flex items-center gap-1 px-2 py-1 rounded border border-dashed border-slate-200 text-slate-400 mb-1" data-testid={`completed-contract-job-${j.id}`}>
                <JobTag kind={j.job_kind || "contract"} className="opacity-60" />
                <span className="text-[10px] truncate flex-1 line-through">{j.name}</span>
                <button type="button" onClick={() => onReopenContractJob?.(j)} className="text-[9px] font-semibold text-blue-600 hover:underline shrink-0" data-testid={`contract-job-reopen-${j.id}`}>{tr("Reopen")}</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* Week View */
function WeekView({ weekDays, technicians, getDispatchesForCell, getTechDayHours, dragWO, dragDispatch, setDragDispatch, onDrop, onMoveDispatch, onToggleGps, doesTechMatchWO, hoverCell, setHoverCell, isTechOff, smartSuggestions, matchedTechIds, onDispatchClick, onOpenWO, onOpenTimeOff, findTimeOffBlock, findTimeOffBlocks, offDrag, startOffDrag, extendDrag, startExtendDrag, selectedKeys, isSelected, onToggleSelect, categories, flashTech, onReorderStops, liveByTech = {} }) {
  return (
    <div className="border border-slate-300 rounded-lg overflow-hidden" style={{ display: "grid", gridTemplateColumns: weekGridCols(weekDays.length), gridAutoRows: "min-content", width: "100%" }}>
      {/* Header row */}
      <WeekCornerHeader dense />
      {weekDays.map(day => <WeekDayHeader key={day.toISOString()} day={day} dense testId={`board-day-${format(day, "yyyy-MM-dd")}`} />)}

      {/* Tech rows */}
      {technicians.map(tech => {
        const match = dragWO ? doesTechMatchWO(tech.id, dragWO) : null;
        const isBestMatch = dragWO && smartSuggestions[dragWO.id]?.length > 0 && smartSuggestions[dragWO.id][0]?.tech_id === tech.id;
        const isSearchHidden = matchedTechIds && !matchedTechIds.has(tech.id);
        return [
          <div key={`label-${tech.id}`} className={`p-1.5 flex items-center gap-1 border-b border-r border-slate-300 bg-white ${dragWO && match === false ? "opacity-30" : ""} ${isBestMatch ? "bg-amber-50" : ""} ${isSearchHidden ? "opacity-20" : ""} ${flashTech === tech.id ? "ring-2 ring-inset ring-violet-500" : ""}`} data-testid={`tech-row-${tech.id}`}>
            <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: tech.color }} />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-medium text-slate-700 truncate">{tech.name}</div>
              {tech.gps_enabled !== false && <TechLiveState live={liveByTech[tech.id]} />}
            </div>
            <button
              onClick={() => onToggleGps(tech.id, tech.gps_enabled !== false)}
              className={`shrink-0 text-[8px] font-semibold ${tech.gps_enabled === false ? "text-slate-400 hover:text-slate-600" : "text-transparent hover:text-slate-400"}`}
              title={tech.gps_enabled === false ? "GPS Off — Click to enable" : "GPS On — Click to disable"}
              data-testid={`gps-toggle-${tech.id}`}
            >
              {tech.gps_enabled === false ? "GPS Off" : "GPS"}
            </button>
            {dragWO && match !== null && <span className={`text-[9px] font-bold shrink-0 ${match ? "text-green-500" : "text-red-400"}`}>{match ? "✓" : "!"}</span>}
            {isBestMatch && <span className="text-[8px] font-semibold text-amber-500 shrink-0">Best</span>}
          </div>,
          ...weekDays.map(day => {
            const dayStr = format(day, "yyyy-MM-dd");
            const cellDisps = getDispatchesForCell(tech.id, day);
            const isPastDay = dayStr < format(new Date(), "yyyy-MM-dd");
            const dayHours = getTechDayHours(tech.id, day);
            const isToday = isSameDay(day, new Date());
            const realOff = isTechOff(tech.id, day);
            const realOffBlock = realOff && findTimeOffBlock ? findTimeOffBlock(tech.id, day) : null;
            // Apply drag-resize preview: while the dispatcher is dragging
            // the right-edge handle of an off block, every cell in that
            // tech's row recomputes its off-state from the drag's source
            // range + currentDate so the preview is visible in real time.
            let isOff = realOff;
            let offBlock = realOffBlock;
            if (offDrag && offDrag.techId === tech.id) {
                const previewEnd = dayStr < offDrag.sourceStart ? offDrag.sourceStart : (offDrag.currentDate < offDrag.sourceStart ? offDrag.sourceStart : offDrag.currentDate);
                const inPreview = dayStr >= offDrag.sourceStart && dayStr <= previewEnd;
                const inOriginal = realOff && realOffBlock?.id === offDrag.blockId;
                if (inPreview) { isOff = true; offBlock = { ...(realOffBlock || {}), reason: offDrag.reason, end_date: previewEnd, start_date: offDrag.sourceStart, id: offDrag.blockId }; }
                else if (inOriginal) { isOff = false; offBlock = null; }
            }
            const isLastDayOfBlock = isOff && offBlock?.end_date === dayStr;
            const isHovered = hoverCell?.techId === tech.id && hoverCell?.date === dayStr;
            // Categories flagged `blocks_dispatch: false` (Admin, Site Visit,
            // Warranty, Contract Job…) mark the day but still accept work
            // orders — they render as a slim banner above the cards instead
            // of taking over the cell.
            const blocksDispatch = isOff ? categoryBlocks(categories, offBlock?.reason) : false;
            const fullOff = isOff && blocksDispatch;
            const softOff = isOff && !blocksDispatch;
            const catColor = isOff ? categoryColor(categories, offBlock?.reason) : null;
            const extraBlocks = (findTimeOffBlocks?.(tech.id, day) || []).filter(b => b.id !== offBlock?.id);
            // Several categories on one day stack as uniform rows that fill the cell.
            const rowLayout = softOff || extraBlocks.length > 0;
            const canDrop = (dragWO && !fullOff) || (dragDispatch && !fullOff);
            const scopeWarning = dragWO && match === false;
            const renderOffBlock = (compact) => {
              // Multi-select wrapper for time-off blocks. Click toggles
              // selection (ctrl/cmd = additive, shift = range with the
              // last clicked card). Stops propagation so the cell's
              // double-click-to-edit still works as before.
              const blockKey = offBlock?.id ? `o:${tech.id}:${offBlock.id}:${offBlock.reason || ""}` : null;
              const sel = blockKey ? isSelected?.(blockKey) : false;
              const detail = [offBlock?.hours ? `${offBlock.hours}h` : "", offBlock?.notes || ""].filter(Boolean).join(" · ");
              const blockNote = (offBlock?.notes || "").trim();
              const blockHours = offBlock?.hours ? `${offBlock.hours}h` : "";
              return (
                <div
                  className={`${compact ? "cat-block rounded border border-black/5 px-1.5 py-0.5 min-h-[18px] flex-1 flex flex-wrap items-center gap-x-1.5" : "flex-1 flex flex-col items-center justify-center min-h-[30px]"} relative group cursor-pointer ${sel ? "ring-2 ring-blue-500 ring-offset-1 rounded" : ""}`}
                  style={compact ? { "--cat": catColor } : undefined}
                  onClick={(e) => {
                    if (!blockKey) return;
                    e.stopPropagation();
                    onToggleSelect?.(blockKey, e, { type: "off", techId: tech.id, date: dayStr });
                  }}
                  data-testid={`off-block-${tech.id}-${dayStr}`}
                  data-selected={sel ? "true" : "false"}
                  title={`${offBlock?.reason || "Off"}${detail ? ` — ${detail}` : ""} · double-click to change`}
                >
                  {!compact && <span className="text-[8px] font-bold uppercase tracking-wide opacity-70">{tr("Off")} </span>}
                  {offBlock?.reason && (
                    <span className={`${compact ? "text-[8px]" : "text-[8px]"} font-bold uppercase truncate max-w-full px-1`} data-testid={`off-reason-${tech.id}-${dayStr}`}>{offBlock.reason}</span>
                  )}
                  {/* iter269 — the dispatcher's instructions used to be
                      hard-truncated here with no way to read the rest.
                      Click the detail line to expand it in place. */}
                  {compact ? (
                    <>
                      {blockNote && (
                        <OffBlockDetail detail={blockNote} note={blockNote} hours="" compact testId={`off-detail-${tech.id}-${dayStr}`} />
                      )}
                      {blockHours && (
                        <span className="text-[8px] font-semibold opacity-80 ml-auto shrink-0" data-testid={`off-hours-${tech.id}-${dayStr}`}>{blockHours}</span>
                      )}
                    </>
                  ) : detail && (
                    <OffBlockDetail
                      detail={detail}
                      note={blockNote}
                      hours={blockHours}
                      compact={false}
                      testId={`off-detail-${tech.id}-${dayStr}`}
                    />
                  )}
                  {/* Right-edge drag handle on the LAST day of the
                      block. Drag right → extends; drag left → shrinks
                      (or removes when crossing back through start).
                      Visible only on hover/drag so it stays clean. */}
                  {isLastDayOfBlock && offBlock && (
                    <div
                      onMouseDown={(e) => { e.stopPropagation(); startOffDrag?.(tech.id, offBlock, e); }}
                      onClick={(e) => e.stopPropagation()}
                      className={`absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize transition-opacity ${offDrag?.techId === tech.id ? "opacity-80" : "opacity-0 group-hover:opacity-60 hover:opacity-90"}`}
                      style={{ backgroundColor: "rgba(59, 130, 246, 0.7)" }}
                      title="Drag right to extend, left to shorten"
                      data-testid={`off-resize-handle-${tech.id}-${dayStr}`}
                    />
                  )}
                </div>
              );
            };
            return (
              <div key={`${tech.id}-${dayStr}`}
                className={`p-0.5 border-b border-r border-slate-300 last:border-r-0 relative transition-all overflow-hidden flex flex-col select-none
                  ${cellDisps.length === 0 && !isOff ? "hover:outline hover:outline-1 hover:outline-dashed hover:-outline-offset-2 hover:outline-slate-400/50" : ""}
                  ${isOff && !offBlock ? "bg-slate-100" : ""}
                  ${!fullOff ? "bg-white" : ""}
                  ${fullOff && offBlock ? "cat-block" : ""}
                  ${isToday && !isOff ? "bg-blue-50/20" : ""}
                  ${isHovered && canDrop && !scopeWarning ? "ring-2 ring-inset ring-blue-500 bg-blue-50 z-10" : ""}
                  ${isHovered && canDrop && scopeWarning ? "ring-2 ring-inset ring-red-400 bg-red-50 z-10" : ""}
                  ${(dragWO || dragDispatch) && !canDrop ? "opacity-20" : ""}
                  ${isSearchHidden ? "opacity-20" : ""}
                `}
                style={fullOff && offBlock ? { "--cat": catColor } : undefined}
                onDragOver={(e) => { if (canDrop) { e.preventDefault(); setHoverCell({ techId: tech.id, date: dayStr }); } }}
                onDragLeave={() => setHoverCell(null)}
                onDrop={(e) => {
                  e.preventDefault(); setHoverCell(null);
                  if (dragDispatch && !fullOff) { onMoveDispatch(dragDispatch, tech.id, day); }
                  else if (canDrop && dragWO) { onDrop(dragWO, tech.id, day); }
                }}
                onDoubleClick={(e) => {
                  // Skip when a drag is in progress so double-tap reorder
                  // gestures don't accidentally pop the time-off menu.
                  if (dragWO || dragDispatch) return;
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  onOpenTimeOff?.(tech.id, day, { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right });
                }}
                title={isOff ? `Off · ${offBlock?.reason || ""} · double-click to change` : "Double-click to add a contract job or time off"}
                data-testid={`cell-${tech.id}-${dayStr}`}>
                {isOff && offBlock && (
                  <div className={`flex flex-col gap-0.5 ${cellDisps.length ? "mb-0.5" : "flex-1"}`} data-testid={`off-stack-${tech.id}-${dayStr}`}>
                    {renderOffBlock(rowLayout)}
                    {/* Extra blocks covering the same day (e.g. a week-long
                        Contract Job plus a one-day Site Visit) — read-only,
                        the primary block above owns edit/resize. */}
                    {extraBlocks.map(b => (
                      <OffBlockBadge key={b.id} block={b} categories={categories} size="sm" testId={`off-extra-${tech.id}-${dayStr}-${b.id}`} />
                    ))}
                  </div>
                )}
                {/* Strip above the cards (as the team knows it): WO numbers left, day total right.
                    Tags live in the card only. */}
                {!fullOff && (cellDisps.length > 0 || dayHours > 0) && (
                  <div className="flex items-center gap-1 pr-0.5 mb-0.5">
                    <div className="flex-1 min-w-0 flex flex-wrap gap-x-1 text-[7px] font-semibold leading-none">
                      {cellDisps.filter(d => !isContract(d) && d.wo_number).map(d => (
                        <button key={d.id} type="button" onClick={(e) => { e.stopPropagation(); onOpenWO?.(d); }}
                          className="text-blue-500 hover:text-blue-700 hover:underline truncate" data-testid={`cell-wo-link-${d.id}`} title={tr("Open Work Order")}>{d.wo_number}</button>
                      ))}
                    </div>
                    {dayHours > 0 && (
                      <span className="text-[7px] font-bold shrink-0 num" data-testid={`day-hours-${tech.id}-${dayStr}`} style={{ color: dayHours > HOURS_PER_DAY ? "#dc2626" : "#16a34a" }}>{dayHours}/{HOURS_PER_DAY}h</span>
                    )}
                  </div>
                )}
                {!fullOff && cellDisps.length > 0 && (
                  <StopStack stops={cellDisps} tech={tech} compact={cellDisps.length > 1} overDay={dayHours > HOURS_PER_DAY}
                    onReorder={cellDisps.length > 1 && !isPastDay ? onReorderStops : undefined}
                    setDragDispatch={setDragDispatch} onDispatchClick={onDispatchClick} startExtendDrag={startExtendDrag}
                    isSelected={isSelected} onToggleSelect={onToggleSelect} dayStr={dayStr} onOpenWO={onOpenWO} />
                )}
              </div>
            );
          })
        ];
      })}
      {technicians.length === 0 && <div className="text-center py-8 text-slate-400 text-sm" style={{ gridColumn: `1 / -1` }}>{tr("No technicians configured.")}</div>}
    </div>
  );
}

const DISPATCH_STATUS_DOT = {
  scheduled: "bg-blue-400",
  en_route: "bg-purple-500",
  on_site: "bg-amber-500",
  done_for_day: "bg-slate-400",
  completed: "bg-green-500",
};

// Status lives in the left band (iter403): solid = scheduled · striped = en route ·
// solid + full-strength border = on site · emerald band + check = done/complete.
function bandStyle(color, st) {
  if (st === "en_route") return { backgroundImage: `repeating-linear-gradient(135deg, ${color} 0 3px, transparent 3px 6px)` };
  if (st === "done_for_day" || st === "completed") return { backgroundColor: "#10b981" };
  return { backgroundColor: color };
}

// One card for every stop — single or stacked. `compact` (stacked days) hides
// customer/parts; everything else (drag, select, extend, popover) is identical.
function StopStack({ stops, tech, compact, overDay, onReorder, ...cardProps }) {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const reorder = onReorder ? {
    dragId, overId, setDragId, setOverId,
    drop: (targetId) => {
      if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
      const ids = stops.map(s => s.id);
      const from = ids.indexOf(dragId), to = ids.indexOf(targetId);
      ids.splice(to, 0, ids.splice(from, 1)[0]);
      onReorder(ids); setDragId(null); setOverId(null);
    },
  } : null;
  return stops.map(d => <StopCard key={d.id} d={d} tech={tech} compact={compact} overDay={overDay} reorder={reorder} {...cardProps} />);
}

function StopCard({ d, tech, setDragDispatch, onDispatchClick, startExtendDrag, isSelected, onToggleSelect, dayStr, onOpenWO, compact = false, overDay = false, reorder = null }) {
  const st = d.dispatch_status || d.wo_status || "scheduled";
  const today = format(new Date(), "yyyy-MM-dd");
  const isPast = d.date < today;
  const cardKey = `d:${d.id}`;
  const sel = isSelected?.(cardKey);
  const color = bandColor(d, tech);
  const done = st === "done_for_day" || st === "completed";
  const onSite = st === "on_site";
  const hint = dispatchMatchHint({ ...d, tech_name_snapshot: tech?.name || d.tech_name_snapshot }, useContext(SearchHiContext));
  const dropTarget = reorder?.dragId && reorder.dragId !== d.id && reorder.overId === d.id;
  return (
    <div className={`relative rounded pl-2 pr-1.5 py-1 mb-0.5 border group cursor-pointer hover:ring-1 hover:ring-blue-400 ${isPast ? "opacity-50 grayscale-[30%]" : ""} ${sel ? "ring-2 ring-blue-500 ring-offset-1 z-[2]" : ""} ${dropTarget ? "ring-2 ring-blue-400" : ""}`}
      draggable={!isPast}
      onDragStart={(e) => { if (isPast) { e.preventDefault(); return; } e.stopPropagation(); setDragDispatch(d); reorder?.setDragId(d.id); }}
      onDragOver={(e) => { if (reorder?.dragId && reorder.dragId !== d.id) { e.preventDefault(); e.stopPropagation(); reorder.setOverId(d.id); } }}
      onDragLeave={() => { if (reorder?.dragId) reorder.setOverId(null); }}
      onDrop={(e) => { if (reorder?.dragId && reorder.dragId !== d.id) { e.preventDefault(); e.stopPropagation(); reorder.drop(d.id); setDragDispatch(null); } }}
      onDragEnd={() => { setDragDispatch(null); reorder?.setDragId(null); reorder?.setOverId(null); }}
      onClick={(e) => {
        e.stopPropagation();
        // Locked history cards open read-only — never select/action them.
        if (isPast) { onDispatchClick(d, e); return; }
        onToggleSelect?.(cardKey, e, { type: "dispatch", techId: tech.id, date: dayStr || d.date });
        onDispatchClick(d, e);
      }}
      style={{ backgroundColor: `${color}10`, borderColor: `${color}30`, boxShadow: onSite ? `0 0 0 1px ${color}` : undefined }}
      title={DISPATCH_STATUS_LABEL[st] || st}
      data-testid={`dispatch-${d.id}`} data-selected={sel ? "true" : "false"} data-kind={isContract(d) ? "contract" : "service"} data-status={st}>
      <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l" style={bandStyle(color, st)} data-testid={`status-band-${d.id}`} />
      <div className={`flex items-start gap-0.5 text-[9px] font-bold leading-tight ${done ? "text-slate-500" : "text-slate-800"}`}>
        {done && <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600 shrink-0 mt-[1px]" data-testid={`status-check-${d.id}`} />}
        <Hi className="block line-clamp-2 [overflow-wrap:anywhere] min-w-0" text={d.wo_title || "Untitled"} />
      </div>
      <div className="flex items-center gap-1 flex-wrap mt-0.5 text-[8px]">
        {d.hours != null && (
          <span className={`font-semibold num shrink-0 ${overDay ? "text-amber-600" : "text-slate-500"}`} title={overDay ? `Day is over ${HOURS_PER_DAY}h` : `${d.hours}h`} data-testid={`stop-hours-${d.id}`}>{d.hours}h</span>
        )}
        <JobTag d={d} testId={`card-tag-${d.id}`} />
        <CrewBadge crew={d.crew} testId={`crew-badge-${d.id}`} />
        {reorder && !isPast && <GripVertical className="w-3 h-3 text-slate-300 shrink-0 cursor-grab ml-auto opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />}
      </div>
      {!compact && d.client_name && (() => {
        const showSite = d.site_name && d.site_name.trim().toLowerCase() !== d.client_name.trim().toLowerCase();
        return <div className="text-[8px] text-slate-600 break-words mt-0.5"><Hi text={d.client_name} />{showSite ? <> · <Hi text={d.site_name} /></> : ""}</div>;
      })()}
      {hint && <div className="text-[8px] text-amber-800 break-words italic" data-testid={`search-hint-${d.id}`}><Hi text={hint} /></div>}
      {!compact && d.parts?.length > 0 && d.parts.map((p, i) => {
        const checked = isPartChecked(p, d.dispatch_card_progress);
        return (
          // eslint-disable-next-line react/no-array-index-key -- dispatch-cell parts list; check state tracked by idx
          <div key={`dcp-${d.id}-${i}`} className={`text-[8px] font-bold flex items-start gap-0.5 ${checked ? "text-emerald-800 bg-emerald-50 rounded px-1" : "text-slate-800"}`} data-testid={`cell-part-${d.id}-${i}`}>
            {checked ? <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600 shrink-0 mt-[1px]" /> : <Wrench className="w-2.5 h-2.5 text-slate-500 shrink-0 mt-[1px]" />}
            <span className="break-words whitespace-normal min-w-0">{Math.round(p.quantity)}x <Hi text={p.description} /></span>
          </div>
        );
      })}
      {!isPast && startExtendDrag && (
        <div onMouseDown={(e) => startExtendDrag(d, e)} onClick={(e) => e.stopPropagation()} onDragStart={(e) => e.preventDefault()}
          className="absolute top-0 right-0 h-full w-1.5 cursor-ew-resize bg-blue-500/0 hover:bg-blue-500/40 transition-colors"
          title="Drag to extend across days" data-testid={`dispatch-extend-handle-${d.id}`} />
      )}
    </div>
  );
}

/* Month View */
function MonthView({ monthWeeks, currentDate, technicians, dispatches, dragWO, dragDispatch, setDragDispatch, onDrop, onMoveDispatch, onRemove, onEdit, onDuplicate, doesTechMatchWO, hoverCell, setHoverCell, isTechOff, onRequestTechPicker, onDispatchClick, showWeekends }) {
  const dayNames = showWeekends ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] : ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const filterWeek = (week) => showWeekends ? week : week.filter(d => d.getDay() !== 0 && d.getDay() !== 6);
  const techMap = useMemo(() => { const m = {}; technicians.forEach(t => { m[t.id] = t; }); return m; }, [technicians]);
  const getForDay = useCallback((day) => { const ds = format(day, "yyyy-MM-dd"); return dispatches.filter(d => d.date === ds); }, [dispatches]);
  return (
    <div className="border border-slate-300 rounded-lg overflow-hidden">
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${dayNames.length}, 1fr)` }}>
        {dayNames.map(d => (
          <div key={d} className="bg-slate-50 p-1.5 text-center text-[9px] uppercase font-semibold text-slate-400 border-b border-r border-slate-300 last:border-r-0">{d}</div>
        ))}
      </div>
      {monthWeeks.map((week, wi) => (
        // eslint-disable-next-line react/no-array-index-key -- calendar weeks are fixed-position rows for the visible month
        <div key={`mw-${wi}`} style={{ display: "grid", gridTemplateColumns: `repeat(${dayNames.length}, 1fr)` }}>
          {filterWeek(week).map(day => {
            const dayStr = format(day, "yyyy-MM-dd");
            const dayDisps = getForDay(day);
            const inMonth = isSameMonth(day, currentDate);
            const isToday = isSameDay(day, new Date());
            const isHovered = hoverCell?.date === dayStr;
            return (
              <div key={day.toISOString()}
                className={`bg-white p-1 min-h-[72px] border-b border-r border-slate-300 last:border-r-0 ${!inMonth ? "opacity-30" : ""} ${isToday ? "bg-blue-50/40" : ""} ${isHovered && (dragWO || dragDispatch) ? "ring-2 ring-inset ring-blue-500 bg-blue-50" : ""}`}
                onDragOver={(e) => { e.preventDefault(); if (dragWO || dragDispatch) setHoverCell({ date: dayStr }); }}
                onDragLeave={() => setHoverCell(null)}
                onDrop={(e) => {
                  e.preventDefault(); setHoverCell(null);
                  if (dragDispatch) {
                    onMoveDispatch(dragDispatch, dragDispatch.tech_id, day);
                    return;
                  }
                  if (!dragWO || !technicians.length) return;
                  onRequestTechPicker(dragWO, day, e);
                }}
                data-testid={`month-cell-${dayStr}`}>
                <div className={`text-[10px] font-semibold mb-0.5 ${isToday ? "text-blue-600" : "text-slate-500"}`}>{format(day, "d")}</div>
                {dayDisps.map(d => {
                  const tech = techMap[d.tech_id];
                  const isPast = d.date < format(new Date(), "yyyy-MM-dd");
                  return (
                    <div key={d.id} className={`rounded px-1.5 py-1 mb-0.5 text-[9px] border group cursor-pointer hover:ring-1 hover:ring-blue-400 ${isPast ? "opacity-50 grayscale-[30%]" : ""}`}
                      draggable={!isPast} onDragStart={(e) => { if (isPast) { e.preventDefault(); return; } e.stopPropagation(); setDragDispatch(d); }} onDragEnd={() => setDragDispatch(null)}
                      onClick={(e) => { e.stopPropagation(); onDispatchClick(d, e); }}
                      style={{ backgroundColor: `${tech?.color || "#3b82f6"}12`, borderColor: `${tech?.color || "#3b82f6"}30` }}
                      data-testid={`month-dispatch-${d.id}`}>
                      <div className="flex items-center gap-0.5">
                        <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: tech?.color || "#3b82f6" }} />
                        <span className="font-bold text-slate-500 shrink-0">{tech?.name?.split(" ")[0] || "?"}</span>
                        <span className="text-slate-400 shrink-0 ml-auto">{d.hours}h</span>
                        {!isPast && <button className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 shrink-0" onClick={(e) => { e.stopPropagation(); onRemove(d.id); }}><X className="w-2.5 h-2.5" /></button>}
                      </div>
                      <div className="text-[8px] text-slate-500 leading-tight">{d.client_name}{d.site_name ? ` - ${d.site_name}` : ""}</div>
                      <div className="font-semibold text-slate-700 leading-tight break-words">{d.wo_title || d.wo_number}</div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* Dispatch Edit Modal — edit materials only. Notes are append-only and
   handled via "+ Add Note" on the dispatch popover (mirroring Field Notes). */
function DispatchEditModal({ dispatch, onSave, onClose }) {
  const [parts, setParts] = useState(dispatch.parts || []);

  const addPart = () => setParts([...parts, { description: "", quantity: 1 }]);
  const updatePart = (idx, field, value) => setParts(prev => prev.map((p, i) => i === idx ? { ...p, [field]: field === "quantity" ? (parseFloat(value) || 1) : value } : p));
  const removePart = (idx) => setParts(prev => prev.filter((_, i) => i !== idx));

  const handleSave = () => {
    const updates = {};
    if (parts.length > 0) updates.parts_override = parts.filter(p => p.description.trim());
    onSave(dispatch.id, updates);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-800 text-sm">{tr("Edit Dispatch")}</h3>
          <div className="text-xs text-slate-500">{dispatch.wo_title} — {dispatch.wo_number}</div>
          <div className="text-[10px] text-slate-400">{dispatch.client_name} · {dispatch.date}</div>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">{tr("Scope / Materials")}</label>
            <div className="space-y-1.5">
              {parts.map((p, i) => (
                // eslint-disable-next-line react/no-array-index-key -- editable parts rows; index is the lookup key for updatePart/removePart
                <div key={`ep-${i}`} className="flex items-center gap-2">
                  <Input value={p.quantity} onChange={(e) => updatePart(i, "quantity", e.target.value)} className="h-7 w-14 text-xs text-center" type="number" min="1" data-testid={`edit-part-qty-${i}`} />
                  <span className="text-xs text-slate-400">x</span>
                  <Input value={p.description} onChange={(e) => updatePart(i, "description", e.target.value)} className="h-7 text-xs flex-1 font-bold" placeholder={tr("Material description")} data-testid={`edit-part-desc-${i}`} />
                  <button onClick={() => removePart(i)} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="h-7 text-xs mt-1.5" onClick={addPart} data-testid="btn-add-edit-part"><Plus className="w-3 h-3 mr-1" />{tr("Add Material")}</Button>
          </div>
          <p className="text-[10px] text-slate-400 italic">Notes are added via the “Notes” button on the dispatch popover so each entry is timestamped.</p>
          <div className="flex gap-2 justify-end pt-2 border-t border-slate-200">
            <Button variant="outline" size="sm" onClick={onClose}>{tr("Cancel")}</Button>
            <Button size="sm" onClick={handleSave} data-testid="btn-save-dispatch-edit">{tr("Save")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* Expandable detail line for a schedule-category block (PTO / Site Visit /
   Contract Job …). Collapsed it stays a single truncated line so the grid
   keeps its density; clicking it reveals the dispatcher's full note. */
function OffBlockDetail({ detail, note, hours, compact, testId }) {
  const [open, setOpen] = useState(false);
  const expandable = note.length > 0;
  if (!expandable) {
    return <span className={`text-[7px] ${compact ? "truncate flex-1 min-w-0" : "truncate max-w-full px-1"} opacity-75`}>{detail}</span>;
  }
  return (
    <span
      className={`text-[7px] ${compact ? "flex-1 min-w-0" : "max-w-full px-1"} opacity-90 cursor-pointer ${open ? "whitespace-pre-wrap break-words" : "truncate block"}`}
      title={`${detail} · click to ${open ? "collapse" : "read the full note"}`}
      onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
      data-testid={testId}
      data-expanded={open ? "true" : "false"}
    >
      {open ? `${hours ? hours + " · " : ""}${note}` : detail}
      <span className="ml-1 font-semibold opacity-70">{open ? "▲" : "▼"}</span>
    </span>
  );
}

/* Field Notes Modal — view/add WO notes from dispatch board */
function FieldNotesModal({ data, onAddNote, onClose }) {
  const [newNote, setNewNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!newNote.trim()) return;
    setSubmitting(true);
    await onAddNote(data.woId, newNote.trim());
    setNewNote("");
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="field-notes-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 shrink-0">
          <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-amber-500" />{tr("Field Notes")}
          </h3>
          <div className="text-xs text-slate-500">{data.woTitle || "Work Order"} — {data.woNumber}</div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {(!data.notes || data.notes.length === 0) && (
            <div className="text-xs text-slate-400 text-center py-6">{tr("No notes yet")}</div>
          )}
          {(data.notes || []).map((n, i) => (
            <div key={n.id || i} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100" data-testid={`note-item-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold text-slate-700">{abbreviateName(n.tech_name || n.created_by)}</span>
                <span className="text-[9px] text-slate-400">{formatNoteDate(n.created_at)}</span>
              </div>
              <div className="text-xs text-slate-600"><NoteText note={n} /></div>
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-slate-200 shrink-0">
          <div className="flex gap-2">
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder={tr("Add a note...")}
              rows={2}
              className="text-xs flex-1"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
              data-testid="field-note-input"
            />
            <Button size="sm" className="shrink-0 self-end" onClick={handleSubmit} disabled={!newNote.trim() || submitting} data-testid="btn-add-field-note">
              {submitting ? "..." : "Add"}
            </Button>
          </div>
          <div className="flex justify-end mt-2">
            <Button variant="outline" size="sm" onClick={onClose}>{tr("Close")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Month View Tech Picker — shown when dropping a WO on a day in month view */
function MonthDropTechPicker({ techs, wo, day, x, y, onSelect, onClose, isTechOff }) {
  const dayStr = format(day, "EEE, MMM d");
  // Position the picker near the drop point
  const style = {
    position: "fixed",
    left: Math.min(x, window.innerWidth - 220),
    top: Math.min(y, window.innerHeight - 300),
    zIndex: 60,
  };

  return (
    <>
      <div className="fixed inset-0 z-50" onClick={onClose} />
      <div style={style} className="bg-white rounded-lg shadow-xl border border-slate-200 w-52 overflow-hidden" data-testid="month-tech-picker">
        <div className="p-2 border-b border-slate-100 bg-slate-50">
          <div className="text-[10px] font-bold text-slate-500 uppercase">{tr("Assign to Tech")}</div>
          <div className="text-xs text-slate-700 font-semibold truncate">{wo.wo_number} — {dayStr}</div>
        </div>
        <div className="max-h-48 overflow-y-auto">
          {techs.map(t => {
            const off = isTechOff(t.id, day);
            return (
              <button key={t.id}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-blue-50 border-b border-slate-50 last:border-0 ${off ? "opacity-40" : ""}`}
                onClick={() => { if (!off) onSelect(t.id); }}
                disabled={off}
                data-testid={`pick-tech-${t.id}`}
              >
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                <span className="font-medium text-slate-700">{t.name}</span>
                {off && <span className="ml-auto text-[9px] text-slate-400">{tr("Off")}</span>}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}


/* Dispatch Popover — click a dispatch in week or month view to see full detail & edit */
function DispatchPopover({ dispatch: d, x, y, technicians, onClose, onRemove, onDuplicate, onNotes, getNextWeekday, onOpenWO, onComplete, onAddDispatchNote, onAddMaterial, onAddFieldNote, onAddStop, onEditContractJob }) {
  const tech = technicians.find(t => t.id === d.tech_id);
  const contract = isContract(d);
  const band = bandColor(d, tech);
  const st = d.dispatch_status || d.wo_status || "scheduled";
  const noteCount = d.field_notes?.length || 0;
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [newMaterial, setNewMaterial] = useState({ description: "", quantity: 1, part_number: "" });
  const [savingMaterial, setSavingMaterial] = useState(false);
  const [showAddFieldNote, setShowAddFieldNote] = useState(false);
  const [newFieldNote, setNewFieldNote] = useState("");
  const [savingFieldNote, setSavingFieldNote] = useState(false);
  const today = format(new Date(), "yyyy-MM-dd");
  const isPast = d.date < today;
  // `notes` is a list of timestamped entries (server normalizes legacy
  // string values to a one-entry list on read). Defensive default.
  const dispatchNotes = Array.isArray(d.notes) ? d.notes : [];
  // iter271 — read receipts. A note added after the tech last opened the
  // panel counts as unread again.
  const noteReceipts = Array.isArray(d.notes_seen) ? d.notes_seen : [];
  const latestDispatchNoteAt = dispatchNotes.reduce((mx, n) => (n?.created_at && n.created_at > mx ? n.created_at : mx), "");
  const readReceipt = noteReceipts.find(r => (r.up_to_note_at || "") >= latestDispatchNoteAt && latestDispatchNoteAt);
  const notesUnread = dispatchNotes.length > 0 && !readReceipt;

  const style = {
    position: "fixed",
    left: Math.min(x, window.innerWidth - 320),
    top: Math.min(y, window.innerHeight - 400),
    zIndex: 60,
  };

  return (
    <>
      <div className="fixed inset-0 z-50" onClick={onClose} />
      <div style={style} className="bg-white rounded-lg shadow-2xl border border-slate-200 w-72 overflow-hidden z-[60]" data-testid="month-dispatch-popover">
        {/* Header */}
        <div className="p-3 border-b border-slate-100 relative" style={{ backgroundColor: `${band}10`, boxShadow: `inset 4px 0 0 ${band}` }}>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: tech?.color || "#3b82f6" }} />
            <span className="text-xs font-bold text-slate-800 truncate flex-1">{d.wo_title || "Untitled"}</span>
            <JobTag d={d} size="sm" testId="popover-job-tag" />
            <span className={`w-2 h-2 rounded-full shrink-0 ${DISPATCH_STATUS_DOT[st] || "bg-slate-300"}`} />
          </div>
          <div className="flex items-center gap-2 mt-1 ml-4">
            {contract
              ? <button onClick={() => onEditContractJob?.(d)} className="text-[9px] text-blue-600 hover:text-blue-700 hover:underline font-semibold" data-testid="popover-contract-edit">{tr("Edit Contract Job")}</button>
              : <button onClick={() => onOpenWO(d)} className="text-[9px] text-blue-600 hover:text-blue-700 hover:underline font-semibold" data-testid="popover-wo-link">{d.wo_number}</button>}
            <span className="text-[9px] text-slate-400">{d.date}</span>
            <span className="text-[9px] text-slate-400">{d.hours}h</span>
            <span className="text-[9px] text-slate-500 font-medium">{DISPATCH_STATUS_LABEL[st]}</span>
          </div>
        </div>

        {/* Details */}
        <div className="p-3 space-y-1.5 max-h-96 overflow-y-auto">
          <div className="text-[10px] text-slate-600">
            <span className="font-semibold">Tech:</span> {tech?.name || "Unknown"}
          </div>
          <div className="text-[10px] text-slate-600">
            <span className="font-semibold">Customer:</span> {d.client_name || "—"}{d.site_name && d.site_name.trim().toLowerCase() !== (d.client_name || "").trim().toLowerCase() ? ` · ${d.site_name}` : ""}
          </div>
          {d.site_address && (
            <div className="flex items-start gap-1 text-[10px] text-slate-600" data-testid="popover-address">
              <MapPin className="w-3 h-3 text-slate-500 shrink-0 mt-[1px]" />
              <span className="break-words min-w-0">{d.site_address}</span>
            </div>
          )}
          {d.contact_name && (
            <div className="flex items-center gap-1 text-[10px] flex-wrap" data-testid="popover-contact">
              <User className="w-3 h-3 text-slate-500 shrink-0" />
              {d.contact_email ? (
                <a href={`mailto:${d.contact_email}`} className="text-blue-600 hover:underline font-medium">{d.contact_name}</a>
              ) : (
                <span className="text-slate-700 font-medium">{d.contact_name}</span>
              )}
              {d.contact_phone && (
                <a href={`tel:${d.contact_phone}`} className="text-blue-600 hover:underline flex items-center gap-0.5">
                  <Phone className="w-3 h-3" />{d.contact_phone}
                </a>
              )}
            </div>
          )}
          {(d.lift_note || d.lift_required != null) && !contract && (
            <div className="flex items-start gap-1 text-[10px] text-slate-700 font-medium" data-testid="popover-lift">
              <span className="text-[11px] shrink-0">▲</span>
              <span className="break-words min-w-0">{d.lift_note || (d.lift_required ? "Lift Required" : "No lift required")}</span>
            </div>
          )}
          {contract && d.install_details && (
            <div className="text-[10px] text-slate-700 whitespace-pre-wrap rounded bg-blue-50 border border-blue-100 px-1.5 py-1" data-testid="popover-install-details">
              <span className="font-semibold text-blue-800 block text-[9px] uppercase tracking-wide">{tr("Install details")}</span>{d.install_details}
            </div>
          )}
          {contract && (
            <div className={`text-[10px] font-semibold flex items-center gap-1 ${d.dot_inspection_required ? (d.dot_inspection_complete ? "text-emerald-700" : "text-red-700") : "text-slate-500"}`} data-testid="popover-dot">
              {d.dot_inspection_required ? (d.dot_inspection_complete ? "DOT inspection complete" : "DOT trailer inspection required") : "No DOT requirement"}
            </div>
          )}
          {!contract && (
          <div className="space-y-0.5">
            {/* Materials — inline "+ Material" form mirrors the "+ Note"
              flow above. Saves through to dispatch.parts_override so the
              tech card immediately reflects what the dispatcher added.
              Section is always shown so the button is reachable even
              when no parts have been promoted to the WO yet. */}
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-semibold text-slate-600">Materials{d.parts?.length ? ` (${d.parts.length})` : ""}</div>
              {!showAddMaterial && !isPast && (
                <button onClick={() => setShowAddMaterial(true)} className="text-[9px] text-blue-600 hover:underline" data-testid="popover-add-material-btn">+ Material</button>
              )}
            </div>
            {(d.parts || []).map((p, i) => {
              const done = isPartChecked(p, d.dispatch_card_progress);
              return (
                // eslint-disable-next-line react/no-array-index-key -- popover material list; check state by idx
                <div key={`pm-${i}`} className={`text-[10px] flex items-center gap-1 ml-1 ${done ? "text-emerald-800 bg-emerald-50 rounded px-1" : "text-slate-700"}`} data-testid={`popover-material-${i}`}>
                  {done
                    ? <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600 shrink-0" />
                    : <Wrench className="w-2.5 h-2.5 text-slate-400 shrink-0" />}
                  <span className="font-bold">{Math.round(p.quantity)}x</span>
                  <span className="break-words min-w-0">{p.description}{p.part_number ? <span className="text-slate-400 ml-1">[{p.part_number}]</span> : null}</span>
                </div>
              );
            })}
            {showAddMaterial && (
              <div className="flex flex-col gap-1 mt-1 bg-slate-50 border border-slate-200 rounded p-1.5">
                <div className="flex items-center gap-1">
                  <Input value={newMaterial.quantity} onChange={e => setNewMaterial(m => ({ ...m, quantity: parseFloat(e.target.value) || 1 }))} type="number" min="1" className="h-6 w-12 text-[10px] text-center" data-testid="popover-material-qty" />
                  <span className="text-[9px] text-slate-400">x</span>
                  <Input value={newMaterial.description} onChange={e => setNewMaterial(m => ({ ...m, description: e.target.value }))} placeholder={tr("Material name")} className="h-6 text-[10px] flex-1" data-testid="popover-material-desc" />
                </div>
                <Input value={newMaterial.part_number} onChange={e => setNewMaterial(m => ({ ...m, part_number: e.target.value }))} placeholder={tr("Part # (optional)")} className="h-6 text-[10px]" data-testid="popover-material-part" />
                <div className="flex justify-end gap-1">
                  <Button variant="outline" size="sm" className="h-5 text-[9px] px-1.5" onClick={() => { setShowAddMaterial(false); setNewMaterial({ description: "", quantity: 1, part_number: "" }); }}>{tr("Cancel")}</Button>
                  <Button size="sm" className="h-5 text-[9px] px-1.5" disabled={savingMaterial || !newMaterial.description.trim()} onClick={async () => {
                    setSavingMaterial(true);
                    try {
                      await onAddMaterial(d, { ...newMaterial, description: newMaterial.description.trim(), part_number: newMaterial.part_number.trim() });
                      setNewMaterial({ description: "", quantity: 1, part_number: "" });
                      setShowAddMaterial(false);
                    } finally { setSavingMaterial(false); }
                  }} data-testid="popover-material-save">{tr("Save")}</Button>
                </div>
              </div>
            )}
          </div>
          )}
          {(dispatchNotes.length > 0 || showAddNote) && (
            <div className="space-y-0.5 border-t border-dashed border-slate-200 pt-1">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-semibold text-slate-600 flex items-center gap-1.5">
                  Dispatch Notes{dispatchNotes.length > 0 ? ` (${dispatchNotes.length})` : ""}
                  {dispatchNotes.length > 0 && (
                    <span
                      className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${
                        notesUnread ? "bg-rose-100 text-rose-700 border border-rose-300"
                                    : "bg-emerald-100 text-emerald-700 border border-emerald-300"}`}
                      data-testid={`popover-notes-read-state-${d.id}`}
                      data-unread={notesUnread ? "true" : "false"}
                    >
                      {notesUnread ? "UNREAD BY TECH" : "READ"}
                    </span>
                  )}
                </div>
                {!showAddNote && (
                  <button onClick={() => setShowAddNote(true)} className="text-[9px] text-blue-600 hover:underline" data-testid="popover-add-dispatch-note-btn">+ Note</button>
                )}
              </div>
              {dispatchNotes.slice(-3).map((n, i) => (
                <div key={n.id || i} className="text-[9px] text-slate-500 italic flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold not-italic text-slate-600">{abbreviateName(n.created_by)}:</span> <NoteText note={n} />
                  </div>
                  {n.created_at && <span className="not-italic text-slate-400 text-[8px] shrink-0 self-start">{formatNoteDate(n.created_at)}</span>}
                </div>
              ))}
              {/* Read receipt — who opened the notes and when. */}
              {noteReceipts.length > 0 && (
                <div className="text-[8px] text-slate-400 pt-0.5" data-testid={`popover-notes-seen-${d.id}`}>
                  {noteReceipts.map((r, i) => (
                    <div key={r.tech_id || i}>
                      Seen by {abbreviateName(r.tech_name)} · {formatNoteDate(r.seen_at)}
                      {latestDispatchNoteAt && (r.up_to_note_at || "") < latestDispatchNoteAt ? " (before the latest note)" : ""}
                    </div>
                  ))}
                </div>
              )}
              {showAddNote && (
                <div className="flex items-start gap-1 mt-1">
                  <Textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder={tr("Add a dispatch note...")} rows={2} className="text-[10px] flex-1" data-testid="popover-dispatch-note-input" />
                  <div className="flex flex-col gap-1">
                    <Button size="sm" className="h-5 text-[9px] px-1.5" disabled={savingNote || !newNote.trim()} onClick={async () => {
                      setSavingNote(true);
                      try { await onAddDispatchNote(d, newNote.trim()); setNewNote(""); setShowAddNote(false); }
                      finally { setSavingNote(false); }
                    }} data-testid="popover-dispatch-note-save">{tr("Save")}</Button>
                    <Button variant="outline" size="sm" className="h-5 text-[9px] px-1.5" onClick={() => { setShowAddNote(false); setNewNote(""); }}>{tr("Cancel")}</Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {dispatchNotes.length === 0 && !showAddNote && (
            <button onClick={() => setShowAddNote(true)} className="text-[9px] text-blue-600 hover:underline border-t border-dashed border-slate-200 pt-1 w-full text-left" data-testid="popover-add-dispatch-note-btn-empty">+ Note</button>
          )}
          {(noteCount > 0 || showAddFieldNote) && (
            <div className="space-y-0.5 border-t border-dashed border-slate-200 pt-1">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-semibold text-slate-600">Field Notes{noteCount > 0 ? ` (${noteCount})` : ""}</div>
                {!showAddFieldNote && (
                  <button onClick={() => setShowAddFieldNote(true)} className="text-[9px] text-blue-600 hover:underline" data-testid="popover-add-field-note-btn">+ Note</button>
                )}
              </div>
              {(d.field_notes || []).map((n, i) => (
                <div key={n.id || i} className="text-[10px] text-slate-500 italic leading-snug flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold not-italic text-slate-600">{abbreviateName(n.tech_name || n.created_by)}:</span> <NoteText note={n} />
                  </div>
                  {n.created_at && <span className="not-italic text-slate-400 text-[9px] shrink-0 self-start">{formatNoteDate(n.created_at)}</span>}
                </div>
              ))}
              {showAddFieldNote && (
                <div className="flex items-start gap-1 mt-1">
                  <Textarea value={newFieldNote} onChange={e => setNewFieldNote(e.target.value)} placeholder={tr("Add a field note...")} rows={2} className="text-[10px] flex-1" data-testid="popover-field-note-input" />
                  <div className="flex flex-col gap-1">
                    <Button size="sm" className="h-5 text-[9px] px-1.5" disabled={savingFieldNote || !newFieldNote.trim()} onClick={async () => {
                      setSavingFieldNote(true);
                      try { await onAddFieldNote(d, newFieldNote.trim()); setNewFieldNote(""); setShowAddFieldNote(false); }
                      finally { setSavingFieldNote(false); }
                    }} data-testid="popover-field-note-save">{tr("Save")}</Button>
                    <Button variant="outline" size="sm" className="h-5 text-[9px] px-1.5" onClick={() => { setShowAddFieldNote(false); setNewFieldNote(""); }}>{tr("Cancel")}</Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {noteCount === 0 && !showAddFieldNote && (
            <button onClick={() => setShowAddFieldNote(true)} className="text-[9px] text-blue-600 hover:underline border-t border-dashed border-slate-200 pt-1 w-full text-left" data-testid="popover-add-field-note-btn-empty">+ Note (Field)</button>
          )}
          {/* Status Timeline */}
          {d.status_history?.length > 0 && (
            <StatusTimeline history={d.status_history} />
          )}
        </div>

        {/* Actions */}
        <div className="p-2 border-t border-slate-100 flex items-center gap-1 flex-wrap">
          {!isPast && onAddStop && (
            <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 gap-1" title="Add another WO or contract job to this tech's day" onClick={() => onAddStop(d)} data-testid="popover-add-stop-btn">
              <Plus className="w-3 h-3" />{tr("Add stop")}
            </Button>
          )}
          {!isPast && (
            <Button variant="outline" size="icon" className="h-6 w-6" title={tr("Copy to next weekday")} onClick={() => { const nd = getNextWeekday(d); onDuplicate(d, nd, d.tech_id); }} data-testid="popover-copy-btn">
              <Copy className="w-3 h-3" />
            </Button>
          )}
          {!isPast && st !== "completed" && !confirmComplete && (
            <Button variant="outline" size="icon" className="h-6 w-6 text-green-600 hover:text-green-700 border-green-200 hover:border-green-300" title={tr("Mark complete")} onClick={() => setConfirmComplete(true)} data-testid="popover-complete-btn">
              <CheckCircle2 className="w-3 h-3" />
            </Button>
          )}
          {!isPast && confirmComplete && (
            <div className="flex items-center gap-1 animate-in fade-in duration-200">
              <span className="text-[9px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">100% Complete?</span>
              <Button size="sm" className="h-6 text-[10px] px-2 bg-green-600 hover:bg-green-700 text-white" onClick={() => { setConfirmComplete(false); onComplete(d); }} data-testid="popover-confirm-complete-btn">
                {tr("Confirm")}
              </Button>
              <button className="text-[9px] text-slate-400 hover:text-slate-600" onClick={() => setConfirmComplete(false)}>
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {!isPast && !confirmComplete && !confirmRemove && (
            <Button variant="outline" size="icon" className="h-6 w-6 text-red-500 hover:text-red-700" title={tr("Remove dispatch")} onClick={() => setConfirmRemove(true)} data-testid="popover-remove-btn">
              <X className="w-3 h-3" />
            </Button>
          )}
          {!isPast && confirmRemove && (
            <div className="flex items-center gap-1 animate-in fade-in duration-200">
              <span className="text-[9px] font-medium text-red-700 bg-red-50 px-1.5 py-0.5 rounded">{tr("Remove dispatch?")}</span>
              <Button size="sm" className="h-6 text-[10px] px-2 bg-red-600 hover:bg-red-700 text-white" onClick={() => { setConfirmRemove(false); onRemove(d.id); }} data-testid="popover-confirm-remove-btn">
                {tr("Yes")}
              </Button>
              <button className="text-[9px] text-slate-400 hover:text-slate-600" onClick={() => setConfirmRemove(false)}>
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {isPast && (
            <span className="text-[9px] text-slate-400 ml-auto">History — locked</span>
          )}
        </div>
      </div>
    </>
  );
}





// ──────────────────────────────────────────────────────────────────
// QueueMaterialAdder — inline "+ Material" button + form for the
// Open WO sidebar card. Saves to wo.dispatch_card_materials so the
// new entry shows up on every WO Card surface (sidebar / board / tech).
// ──────────────────────────────────────────────────────────────────
function QueueMaterialAdder({ woId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ description: "", quantity: 1, part_number: "" });
  const [saving, setSaving] = useState(false);

  if (!open) {
    return (
      <button onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="text-[9px] text-blue-600 hover:underline" data-testid={`queue-add-material-btn-${woId}`}>
        + Material
      </button>
    );
  }
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-1.5 space-y-1" onClick={e => e.stopPropagation()}>
      <div className="flex items-center gap-1">
        <Input value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: parseFloat(e.target.value) || 1 }))} type="number" min="1" className="h-6 w-12 text-[10px] text-center" data-testid={`queue-material-qty-${woId}`} />
        <span className="text-[9px] text-slate-400">x</span>
        <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={tr("Material name")} className="h-6 text-[10px] flex-1" data-testid={`queue-material-desc-${woId}`} />
      </div>
      <Input value={form.part_number} onChange={e => setForm(f => ({ ...f, part_number: e.target.value }))} placeholder={tr("Part # (optional)")} className="h-6 text-[10px]" data-testid={`queue-material-part-${woId}`} />
      <div className="flex justify-end gap-1">
        <Button variant="outline" size="sm" className="h-5 text-[9px] px-1.5" onClick={() => { setOpen(false); setForm({ description: "", quantity: 1, part_number: "" }); }}>{tr("Cancel")}</Button>
        <Button size="sm" className="h-5 text-[9px] px-1.5" disabled={saving || !form.description.trim()} onClick={async () => {
          setSaving(true);
          try {
            const res = await addDispatchCardMaterial(woId, {
              description: form.description.trim(),
              quantity: form.quantity,
              part_number: form.part_number.trim(),
              created_by: localStorage.getItem("abs_current_user") || "Dispatch",
            });
            const saved = res?.data || form;
            onAdded?.(woId, saved);
            setForm({ description: "", quantity: 1, part_number: "" });
            setOpen(false);
            toast.success("Material added");
          } catch {
            toast.error("Failed to add material");
          } finally { setSaving(false); }
        }} data-testid={`queue-material-save-${woId}`}>{tr("Save")}</Button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// QueueFieldNoteAdder — inline "+ Note" button + textarea, saves to
// wo.field_notes (already WO-level, so propagates to all cards).
// ──────────────────────────────────────────────────────────────────
function QueueFieldNoteAdder({ woId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open) {
    return (
      <button onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="text-[9px] text-blue-600 hover:underline ml-2" data-testid={`queue-add-note-btn-${woId}`}>
        + Note
      </button>
    );
  }
  return (
    <div className="flex items-start gap-1" onClick={e => e.stopPropagation()}>
      <Textarea value={text} onChange={e => setText(e.target.value)} placeholder={tr("Add a note...")} rows={2} className="text-[10px] flex-1" data-testid={`queue-note-input-${woId}`} />
      <div className="flex flex-col gap-1">
        <Button size="sm" className="h-5 text-[9px] px-1.5" disabled={saving || !text.trim()} onClick={async () => {
          setSaving(true);
          try {
            const techName = localStorage.getItem("abs_current_user") || "Dispatch";
            const res = await addFieldNote(woId, { text: text.trim(), tech_name: techName });
            const saved = res?.data || { id: `tmp-${Date.now()}`, text: text.trim(), tech_name: techName, created_at: new Date().toISOString() };
            onAdded?.(woId, saved);
            setText("");
            setOpen(false);
            toast.success("Note added");
          } catch {
            toast.error("Failed to save note");
          } finally { setSaving(false); }
        }} data-testid={`queue-note-save-${woId}`}>{tr("Save")}</Button>
        <Button variant="outline" size="sm" className="h-5 text-[9px] px-1.5" onClick={() => { setOpen(false); setText(""); }}>{tr("Cancel")}</Button>
      </div>
    </div>
  );
}
